import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  ShoppingCart, User, LogOut, Shield, Plus, Trash2, Edit, Edit2,
  Package, Users, Gavel, DollarSign, Clock, CheckCircle, XCircle,
  Upload, RefreshCw, AlertTriangle, Truck, MapPin, Copy, X, Home, Mail,
  ChevronDown, ChevronUp, Tag, Zap, MessageSquare, Radio, Timer, Image, GripVertical, Check, ChevronsUpDown, Palette, BarChart3, Fingerprint, Calendar, Eye, EyeOff, Share2, Wrench, Database, Printer, Handshake
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { errMsg } from '../lib/utils';
import { useCart } from '../context/CartContext';

// Extracted admin components
import CountdownTimer from '../components/admin/CountdownTimer';
import OrderCard, { STATUS_CONFIG } from '../components/admin/OrderCard';
import AdminNotesBulkBar from '../components/admin/AdminNotesBulkBar';
import OrderNumberManager from '../components/admin/OrderNumberManager';
import DropHistoryManager from '../components/admin/DropHistoryManager';
import ProductOrderManager from '../components/admin/ProductOrderManager';
import DraggableToolGrid from '../components/admin/DraggableToolGrid';
import AdminNotifications from '../components/admin/AdminNotifications';
import CrashUptimeWidget from '../components/admin/CrashUptimeWidget.jsx';
import InboundWidget from '../components/admin/InboundWidget.jsx';
import ConsignmentWidget from '../components/admin/ConsignmentWidget.jsx';

const API = `${'https://shopblkmrkt.com'}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

const CATEGORIES = ['Decks', 'Wheels', 'Grip Tape', 'Obstacles', 'Accessories', 'Bundles'];

// Append ?size=thumb (or whatever) to /api/images/* URLs so the browser
// downloads a ~10–100KB resized JPG instead of the 10MB+ original. Crucial
// for the admin upload preview grid: rendering 5+ full-res iPhone photos in
// 100px tiles allocates GB of RGBA framebuffer and OOM-crashes the tab.
const thumbUrl = (url, size = 'thumb') => {
  if (!url || typeof url !== 'string') return url;
  // Only resize images we serve; leave external/CDN URLs alone
  if (!url.includes('/api/images/')) return url;
  return url + (url.includes('?') ? '&' : '?') + 'size=' + size;
};

// Helper to format time to Eastern Time
const formatToEST = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }) + ' EST';
};

// Helper to format date for datetime-local input (local timezone)
const formatForDateTimeInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function AdminDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [combineShippingMode, setCombineShippingMode] = useState(false);
  const [bulkTrackingOpen, setBulkTrackingOpen] = useState(false);
  const [bulkTracking, setBulkTracking] = useState({ tracking_number: '', shipping_carrier: 'usps' });
  const [offers, setOffers] = useState([]);
  const [offerCounts, setOfferCounts] = useState({});
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [paymentPlanCounts, setPaymentPlanCounts] = useState({});
  const [dropConfig, setDropConfig] = useState({ is_open: false, access_code: '', drop_name: '', scheduled_drop_time: '' });
  const [reminderStats, setReminderStats] = useState({ total_subscribers: 0, sent: 0, pending: 0 });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboardToolOrder, setDashboardToolOrder] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  // Read tab from URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'overview');
  
  // Sync URL when tab changes
  useEffect(() => {
    if (activeTab !== 'overview') {
      setSearchParams({ tab: activeTab });
    } else {
      setSearchParams({});
    }
  }, [activeTab, setSearchParams]);
  
  // Check for prefilled product data from submissions page
  useEffect(() => {
    const prefillData = sessionStorage.getItem('prefill_product');
    const action = searchParams.get('action');
    
    if (prefillData && action === 'create') {
      try {
        const data = JSON.parse(prefillData);
        setProductForm(prev => ({
          ...prev,
          name: data.name || '',
          description: data.description || '',
          category: data.category?.toUpperCase() || prev.category,
          brand: data.brand || '',
          images: data.images || [],
          image_url: data.images?.[0] || '',
          cost: data.cost || '',
          price: data.price || prev.price || '',
          // Consignment linking — critical so the saved product gets tagged
          // and the sale-hook can credit the consignor when it sells.
          is_consignment: !!data.is_consignment,
          consignment_item_id: data.consignment_item_id || null,
          consignor_user_id: data.consignor_user_id || null,
          _consignment_meta: data._consignment_meta || null,
        }));
        setProductDialogOpen(true);
        sessionStorage.removeItem('prefill_product');
        // Remove action param from URL
        searchParams.delete('action');
        setSearchParams(searchParams);
      } catch (e) {
        console.error('Failed to parse prefill product data:', e);
      }
    }
  }, [searchParams, setSearchParams]);
  
  // Product form
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: '', brand: '', image_url: '',
    images: [], // Multiple images support
    stock: '1', is_bidding: false, reserve_price: '', buy_now_price: '', bid_end_time: '',
    has_variants: false, variant_type: 'size', variants: [],
    is_one_time: false,
    lot_number: 'LOT 1', lot_type: 'preset', // LOT management
    visibility: 'offline', // 'online', 'offline', or 'coming_soon'
    allow_offers: false, // Allow price negotiation on this product
    // New payment plan options
    flex_pay_enabled: false, // 25% down, 7 days, with interest
    flex_pay_interest: 15, // Interest percentage (10-20%)
    standard_plan_enabled: false, // 50% down, 14 days, 3-4 payments, $350+ only
    standard_plan_installments: 3, // Number of installments for standard plan
    cost: '', // Admin-only: what we paid for the item
    // Consignment linking — set when this product is being created from a
    // consignment item. Cleared when the form resets.
    is_consignment: false,
    consignment_item_id: null,
    consignor_user_id: null,
    _consignment_meta: null,
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Image upload state (cropping removed for faster workflow)
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  
  // Product search/filter state
  const [productSearch, setProductSearch] = useState('');
  const [productBrandFilter, setProductBrandFilter] = useState('all');
  const [productCostFilter, setProductCostFilter] = useState('all'); // 'all', 'missing', 'has'
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantStock, setNewVariantStock] = useState('1');
  const [newVariantCost, setNewVariantCost] = useState('');
  const [newVariantPrice, setNewVariantPrice] = useState('');
  
  // Brand management
  const [brands, setBrands] = useState([]);
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandColor, setNewBrandColor] = useState('#2DD4BF');
  const [showNewBrandForm, setShowNewBrandForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showImageTools, setShowImageTools] = useState(false);
  const [showProductOrder, setShowProductOrder] = useState(false);
  
  // LOT management
  const [lots, setLots] = useState([]);
  const [lastLot, setLastLot] = useState('LOT 1');
  
  // Temporary Access Codes
  const [tempCodes, setTempCodes] = useState([]);
  const [tempCodeForm, setTempCodeForm] = useState({ code: '', duration_minutes: 30, max_uses: '', note: '' });
  const [showTempCodeDialog, setShowTempCodeDialog] = useState(false);
  const [selectedTempCode, setSelectedTempCode] = useState(null);
  
  // Category Management
  const [adminCategories, setAdminCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  
  // Stock change confirmation state
  const [pendingStockChange, setPendingStockChange] = useState(null);
  // pendingStockChange = { variantId, variantName, oldStock, newStock }

  // Restock notifications
  const [restockNotifications, setRestockNotifications] = useState({});

  // Data Integrity Tools
  const [dataToolsOpen, setDataToolsOpen] = useState(false);
  const [dataToolsLoading, setDataToolsLoading] = useState(false);
  const [soldCoverPhotos, setSoldCoverPhotos] = useState([]);
  const [missingCostItems, setMissingCostItems] = useState(null);
  const [recentOrderCosts, setRecentOrderCosts] = useState(null);
  const [brandIntegrityResults, setBrandIntegrityResults] = useState(null);
  const [orderingResults, setOrderingResults] = useState(null);
  
  // Order Brand/Category Fix Tool
  const [missingBrandCategoryData, setMissingBrandCategoryData] = useState(null);
  const [fixingItems, setFixingItems] = useState({});
  
  // Email Settings
  const [emailSettings, setEmailSettings] = useState({ shipping_email_enabled: true });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  
  // Resend email tool
  const [resendOrderId, setResendOrderId] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendResult, setResendResult] = useState(null);

  // Order update
  const [orderUpdate, setOrderUpdate] = useState({ status: '', paypal_transaction_id: '' });

  // Retry wrapper for API calls
  const fetchWithRetry = async (url, headers, retries = 2) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const res = await axios.get(url, { headers, timeout: 10000 });
        return res;
      } catch (e) {
        if (i === retries) {
          console.error(`Failed after ${retries + 1} attempts: ${url}`, e);
          throw e;
        }
        await new Promise(r => setTimeout(r, 500 * (i + 1))); // Wait before retry
      }
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      
      // Check if we have auth
      if (!headers.Authorization) {
        console.error('No auth token available');
        toast.error('Session expired - please login again');
        setLoading(false);
        return;
      }
      
      const [statsRes, productsRes, ordersRes, dropRes, mailingRes, needsReauctionRes, brandsRes, lotsRes, lastLotRes, tempCodesRes, categoriesRes, offersRes, paymentPlansRes, reminderStatsRes, unreadRes, restockRes] = await Promise.all([
        fetchWithRetry(`${API}/admin/stats`, headers).catch(e => { console.error('Stats failed:', e); return { data: {} }; }),
        fetchWithRetry(`${API}/admin/products-with-cost?limit=500`, headers).catch(e => { console.error('Products failed:', e); return { data: [] }; }),
        fetchWithRetry(`${API}/admin/orders`, headers).catch(e => { console.error('Orders failed:', e); return { data: [] }; }),
        fetchWithRetry(`${API}/drop/status`, headers).catch(e => { console.error('Drop failed:', e); return { data: {} }; }),
        fetchWithRetry(`${API}/admin/mailing-list`, headers).catch(() => ({ data: { count: 0 } })),
        fetchWithRetry(`${API}/admin/products/needs-reauction`, headers).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/brands`).catch(() => ({ data: [] })),
        fetchWithRetry(`${API}/admin/lots`, headers).catch(() => ({ data: [] })),
        fetchWithRetry(`${API}/admin/lots/last`, headers).catch(() => ({ data: { lot_number: 'LOT 1' } })),
        fetchWithRetry(`${API}/admin/temp-codes`, headers).catch(() => ({ data: [] })),
        fetchWithRetry(`${API}/admin/categories`, headers).catch(() => ({ data: [] })),
        fetchWithRetry(`${API}/admin/offers`, headers).catch(() => ({ data: { offers: [], counts: {} } })),
        fetchWithRetry(`${API}/admin/payment-plans`, headers).catch(() => ({ data: { plans: [], counts: {} } })),
        fetchWithRetry(`${API}/admin/drop/reminders/stats`, headers).catch(() => ({ data: { total_subscribers: 0, sent: 0, pending: 0 } })),
        fetchWithRetry(`${API}/admin/contact-messages/unread-count`, headers).catch(() => ({ data: { unread_count: 0 } })),
        fetchWithRetry(`${API}/admin/restock-notifications`, headers).catch(() => ({ data: { by_product: [] } }))
      ]);
      setStats({ 
        ...statsRes.data, 
        blk_list_count: mailingRes.data?.count || 0,
        needs_reauction_count: needsReauctionRes.data?.count || 0
      });
      setProducts(productsRes.data || []);
      // Fetch sold cover photos
      try {
        const soldCoversRes = await axios.get(`${API}/admin/products/sold-cover-photos`, { headers: getAuthHeader() });
        setSoldCoverPhotos(soldCoversRes.data?.flagged || []);
      } catch (e) { /* ignore if endpoint not available */ }
      setOrders(ordersRes.data || []);
      setDropConfig(dropRes.data || {});
      setBrands(brandsRes.data || []);
      setLots(lotsRes.data || []);
      setLastLot(lastLotRes.data?.lot_number || 'LOT 1');
      setTempCodes(tempCodesRes.data || []);
      setAdminCategories(categoriesRes.data || []);
      setOffers(offersRes.data?.offers || []);
      setOfferCounts(offersRes.data?.counts || {});
      setPaymentPlans(paymentPlansRes.data?.plans || []);
      setPaymentPlanCounts(paymentPlansRes.data?.counts || {});
      setReminderStats(reminderStatsRes.data || { total_subscribers: 0, sent: 0, pending: 0 });
      setUnreadMessages(unreadRes.data?.unread_count || 0);
      // Build restock notifications map by product_id
      const restockMap = {};
      (restockRes.data?.by_product || []).forEach(item => {
        restockMap[item.product_id] = item.subscriber_count;
      });
      setRestockNotifications(restockMap);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch dashboard preferences separately
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const headers = getAuthHeader();
        if (!headers.Authorization) return;
        const res = await axios.get(`${API}/admin/preferences/dashboard`, { headers });
        if (res.data?.tool_order) {
          setDashboardToolOrder(res.data.tool_order);
        }
      } catch (e) {
        console.error('Failed to fetch dashboard preferences:', e);
      } finally {
        setPrefsLoaded(true);
      }
    };
    fetchPreferences();
  }, [getAuthHeader]);

  // Fetch email settings
  useEffect(() => {
    const fetchEmailSettings = async () => {
      try {
        const headers = getAuthHeader();
        if (!headers.Authorization) return;
        const res = await axios.get(`${API}/admin/email-settings`, { headers });
        setEmailSettings(res.data || { shipping_email_enabled: true });
      } catch (e) {
        console.error('Failed to fetch email settings:', e);
      }
    };
    fetchEmailSettings();
  }, [getAuthHeader]);

  // Toggle shipping email setting
  const toggleShippingEmail = async () => {
    try {
      setEmailSettingsLoading(true);
      const newValue = !emailSettings.shipping_email_enabled;
      await axios.put(`${API}/admin/email-settings`, 
        { shipping_email_enabled: newValue },
        { headers: getAuthHeader() }
      );
      setEmailSettings(prev => ({ ...prev, shipping_email_enabled: newValue }));
      toast.success(newValue ? 'SHIPPING EMAILS ENABLED' : 'SHIPPING EMAILS DISABLED');
    } catch (e) {
      toast.error('Failed to update email settings');
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  // Send test shipping email
  const sendTestShippingEmail = async () => {
    try {
      setEmailSettingsLoading(true);
      const res = await axios.post(`${API}/admin/test-shipping-email`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error('Failed to send test email');
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  // Save dashboard tool order
  const handleToolOrderChange = async (newOrder) => {
    setDashboardToolOrder(newOrder);
    try {
      const headers = getAuthHeader();
      await axios.put(`${API}/admin/preferences/dashboard`, { tool_order: newOrder }, { headers });
    } catch (e) {
      console.error('Failed to save dashboard preferences:', e);
    }
  };

  // Resend payment confirmation email
  const handleResendPaymentConfirmation = async () => {
    if (!resendOrderId.trim()) {
      toast.error('ENTER AN ORDER ID');
      return;
    }
    
    try {
      setResendLoading(true);
      setResendResult(null);
      const res = await axios.post(
        `${API}/admin/resend-payment-confirmation/${resendOrderId.trim()}`,
        {},
        { headers: getAuthHeader() }
      );
      if (res.data.success) {
        setResendResult({ success: true, message: res.data.message });
        toast.success(res.data.message);
        setResendOrderId('');
      } else {
        setResendResult({ success: false, message: res.data.message });
        toast.error(res.data.message);
      }
    } catch (e) {
      const msg = errMsg(e, 'Failed to send email');
      setResendResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setResendLoading(false);
    }
  };

  const handleDropConfigUpdate = async () => {
    try {
      await axios.put(`${API}/admin/drop`, dropConfig, { headers: getAuthHeader() });
      toast.success('DROP CONFIG UPDATED');
    } catch (error) {
      toast.error('Failed to update drop config');
    }
  };

  const [productSubmitting, setProductSubmitting] = useState(false);
  
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (productSubmitting) return;
    
    // Validate required fields
    if (!productForm.name?.trim()) {
      toast.error('Product name is required');
      return;
    }
    
    if (!productForm.brand) {
      toast.error('Please select a brand');
      return;
    }
    
    if (!productForm.category) {
      toast.error('Please select a category');
      return;
    }
    
    // Cost is required for all products EXCEPT gift cards and BLKCRD
    const isDigitalProduct = productForm.is_gift_card || productForm.is_blkcrd;
    if (!isDigitalProduct && !productForm.cost && productForm.cost !== 0) {
      toast.error('COST is required (what you paid for this item)');
      return;
    }
    
    setProductSubmitting(true);
    try {
      // Calculate total stock from variants if using variants
      let totalStock = parseInt(productForm.stock);
      if (productForm.has_variants && productForm.variants.length > 0) {
        totalStock = productForm.variants.reduce((sum, v) => sum + (v.stock || 0), 0);
      }
      
      const data = {
        name: productForm.name,
        description: productForm.description,
        category: productForm.category,
        brand: productForm.brand || null,
        price: parseFloat(productForm.price),
        stock: totalStock,
        reserve_price: productForm.reserve_price ? parseFloat(productForm.reserve_price) : null,
        buy_now_price: productForm.buy_now_price ? parseFloat(productForm.buy_now_price) : null,
        // Convert local datetime-local format to ISO UTC for storage
        bid_end_time: productForm.bid_end_time ? (() => {
          // If already ISO format, use as-is
          if (productForm.bid_end_time.includes('Z') || productForm.bid_end_time.includes('+')) {
            return productForm.bid_end_time;
          }
          // Convert datetime-local (treated as local/Eastern time) to ISO UTC
          const localDate = new Date(productForm.bid_end_time);
          return localDate.toISOString();
        })() : null,
        is_bidding: productForm.is_bidding,
        has_variants: productForm.has_variants,
        variant_type: productForm.has_variants ? productForm.variant_type : null,
        variants: productForm.has_variants ? productForm.variants : null,
        is_one_time: productForm.is_one_time,
        images: productForm.images.length > 0 ? productForm.images : null,
        image_url: productForm.images.length > 0 ? productForm.images[0] : productForm.image_url,
        lot_number: productForm.lot_number,
        visibility: productForm.visibility,
        allow_offers: productForm.allow_offers && !productForm.is_bidding && !productForm.has_variants,
        // New payment plan options
        flex_pay_enabled: productForm.flex_pay_enabled && !productForm.is_bidding,
        flex_pay_interest: productForm.flex_pay_interest || 15,
        standard_plan_enabled: productForm.standard_plan_enabled && !productForm.is_bidding,
        standard_plan_installments: productForm.standard_plan_installments || 3,
        // Special product types
        is_gift_card: productForm.is_gift_card || false,
        is_blkcrd: productForm.is_blkcrd || false,
        // Admin-only cost tracking
        cost: productForm.cost ? parseFloat(productForm.cost) : 0,
        // Consignment linking — when set, the backend wires the new product
        // to the consignment item, flips its status to LISTED, and sets the
        // 45-day expiry. Without these flags the sale-hook can't credit the
        // consignor's wallet on sale.
        is_consignment: !!productForm.is_consignment,
        consignment_item_id: productForm.consignment_item_id || null,
        consignor_user_id: productForm.consignor_user_id || null,
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data, { headers: getAuthHeader() });
        toast.success('PRODUCT UPDATED');
      } else {
        await axios.post(`${API}/admin/products`, data, { headers: getAuthHeader() });
        toast.success('PRODUCT CREATED');
      }
      
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to save product'));
    } finally {
      setProductSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    // Use custom confirm modal instead of window.confirm (better mobile support)
    setDeleteProductConfirm({ open: true, productId });
  };

  const [deleteProductConfirm, setDeleteProductConfirm] = useState({ open: false, productId: null });

  const confirmDeleteProduct = async () => {
    const productId = deleteProductConfirm.productId;
    if (!productId) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: getAuthHeader() });
      toast.success('PRODUCT DELETED');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
    setDeleteProductConfirm({ open: false, productId: null });
  };

  const [duplicateConfirm, setDuplicateConfirm] = useState({ open: false, productId: null, productName: '' });

  const handleDuplicateProduct = async (productId) => {
    try {
      const response = await axios.post(`${API}/admin/products/${productId}/duplicate`, {}, { headers: getAuthHeader() });
      toast.success('PRODUCT DUPLICATED (OFFLINE) - Edit to customize');
      // Open the edit dialog with the duplicated product
      openEditDialog(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate product');
    }
    setDuplicateConfirm({ open: false, productId: null, productName: '' });
  };

  const confirmDuplicate = (product) => {
    setDuplicateConfirm({ open: true, productId: product.id, productName: product.name });
  };

  // Cycle product visibility: offline -> coming_soon -> online -> offline
  const handleToggleVisibility = async (product) => {
    const currentVisibility = product.visibility || (product.is_online === false ? 'offline' : 'online');
    const nextVisibility = currentVisibility === 'offline' ? 'coming_soon' : 
                           currentVisibility === 'coming_soon' ? 'online' : 'offline';
    
    // INSTANT UI update - before API call
    setProducts(prevProducts => 
      prevProducts.map(p => 
        p.id === product.id 
          ? { ...p, visibility: nextVisibility, is_online: nextVisibility === 'online' }
          : p
      )
    );
    
    const statusLabel = nextVisibility === 'online' ? 'ONLINE' : 
                        nextVisibility === 'coming_soon' ? 'COMING SOON' : 'OFFLINE';
    toast.success(`${product.name} → ${statusLabel}`);
    
    try {
      await axios.put(`${API}/admin/products/${product.id}`, 
        { visibility: nextVisibility },
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Toggle visibility error:', error);
      toast.error('Failed to save - reverting');
      // Revert on error
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === product.id 
            ? { ...p, visibility: currentVisibility, is_online: currentVisibility === 'online' }
            : p
        )
      );
    }
  };

  // Bulk tracking - apply same tracking to multiple orders
  const handleBulkTracking = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Select orders first');
      return;
    }
    if (!bulkTracking.tracking_number) {
      toast.error('Enter tracking number');
      return;
    }
    try {
      const res = await axios.put(`${API}/orders/bulk-tracking`, {
        order_ids: selectedOrders,
        tracking_number: bulkTracking.tracking_number,
        shipping_carrier: bulkTracking.shipping_carrier
      }, { headers: getAuthHeader() });
      
      toast.success(`Applied tracking to ${res.data.updated_count} orders`);
      setSelectedOrders([]);
      setCombineShippingMode(false); // Exit combine mode after applying
      setBulkTrackingOpen(false);
      setBulkTracking({ tracking_number: '', shipping_carrier: 'usps' });
      fetchData();
    } catch (error) {
      toast.error('Failed to apply tracking');
    }
  };

  // Toggle Buy It Now for auction items
  const handleToggleBuyNow = async (product, enabled) => {
    try {
      let newPrice = null;
      if (enabled) {
        // Prompt for BIN price
        const priceInput = window.prompt(
          `Enter Buy It Now price for "${product.name}":`,
          product.buy_now_price?.toString() || (product.price * 1.5).toFixed(2)
        );
        if (!priceInput || isNaN(parseFloat(priceInput))) {
          toast.error('Invalid price entered');
          return;
        }
        newPrice = parseFloat(priceInput);
      }

      await axios.put(
        `${API}/admin/products/${product.id}`,
        { buy_now_price: newPrice },
        { headers: getAuthHeader() }
      );
      toast.success(enabled ? `Buy It Now set to $${newPrice.toFixed(2)}` : 'Buy It Now disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update Buy It Now');
    }
  };

  const handleOrderStatusUpdate = async (orderId, status, paypalId, trackingNumber, shippingCarrier, shippingMethod) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}`,
        { 
          status, 
          paypal_transaction_id: paypalId || null,
          tracking_number: trackingNumber || null,
          shipping_carrier: shippingCarrier || null,
          shipping_method: shippingMethod || null
        },
        { headers: getAuthHeader() }
      );
      toast.success('ORDER UPDATED');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const resetProductForm = () => {
    // Get the first category from admin categories or fallback to 'DECKS'
    const defaultCategory = adminCategories.length > 0 ? adminCategories[0].name : 'DECKS';
    setProductForm({
      name: '', description: '', price: '', category: defaultCategory, brand: '', image_url: '',
      images: [],
      stock: '1', is_bidding: false, reserve_price: '', buy_now_price: '', bid_end_time: '',
      has_variants: false, variant_type: 'size', variants: [],
      is_one_time: false,
      lot_number: lastLot, lot_type: 'preset', // Default to last used LOT
      visibility: 'offline', // 'online', 'offline', or 'coming_soon'
      allow_offers: false,
      flex_pay_enabled: false,
      flex_pay_interest: 15,
      standard_plan_enabled: false,
      standard_plan_installments: 3,
      is_gift_card: false,
      is_blkcrd: false,
      cost: '', // Admin-only cost tracking
      is_consignment: false,
      consignment_item_id: null,
      consignor_user_id: null,
      _consignment_meta: null,
    });
    setEditingProduct(null);
    setNewVariantName('');
    setNewVariantStock('1');
    setNewVariantCost('');
    setShowNewBrandForm(false);
  };

  const openEditDialog = async (product) => {
    setEditingProduct(product);
    
    // Fetch product from admin endpoint to get cost field
    let productWithCost = product;
    try {
      const res = await axios.get(`${API}/admin/products/${product.id}`, { headers: getAuthHeader() });
      productWithCost = res.data;
    } catch (err) {
      console.error('Failed to fetch product cost:', err);
    }
    
    // Ensure mutual exclusivity: if both are somehow set, prioritize auction
    const isAuction = productWithCost.is_bidding || false;
    const hasVariants = isAuction ? false : (productWithCost.has_variants || false);
    
    // Determine lot_type based on current lot_number (case-insensitive check)
    const presetLots = ['LOT 1', 'LOT 2', 'LOT 3', 'LOT 4', 'LOT 5'];
    const normalizedLotNumber = (productWithCost.lot_number || 'LOT 1').toUpperCase();
    const isPresetLot = presetLots.includes(normalizedLotNumber);
    
    setProductForm({
      name: productWithCost.name,
      description: productWithCost.description,
      price: productWithCost.price.toString(),
      category: productWithCost.category?.toUpperCase() || '',
      brand: (productWithCost.brand && productWithCost.brand !== 'None') ? productWithCost.brand : '',
      image_url: productWithCost.image_url || '',
      stock: productWithCost.stock.toString(),
      is_bidding: isAuction,
      reserve_price: productWithCost.reserve_price?.toString() || '',
      buy_now_price: productWithCost.buy_now_price?.toString() || '',
      // Convert ISO time to local datetime-local format for editing
      bid_end_time: productWithCost.bid_end_time ? formatForDateTimeInput(new Date(productWithCost.bid_end_time)) : '',
      has_variants: hasVariants,
      variant_type: productWithCost.variant_type || 'size',
      variants: hasVariants ? (productWithCost.variants || []) : [],
      is_one_time: productWithCost.is_one_time || false,
      images: productWithCost.images || [],
      lot_number: isPresetLot ? normalizedLotNumber : (productWithCost.lot_number || 'LOT 1'),
      lot_type: isPresetLot ? 'preset' : 'custom',
      visibility: productWithCost.visibility || (productWithCost.is_online === false ? 'offline' : 'online'),
      allow_offers: productWithCost.allow_offers || false,
      flex_pay_enabled: productWithCost.flex_pay_enabled || false,
      flex_pay_interest: productWithCost.flex_pay_interest || 15,
      standard_plan_enabled: productWithCost.standard_plan_enabled || false,
      standard_plan_installments: productWithCost.standard_plan_installments || 3,
      is_gift_card: productWithCost.is_gift_card || false,
      is_blkcrd: productWithCost.is_blkcrd || false,
      cost: productWithCost.cost?.toString() || '', // Admin-only cost field
      // Preserve consignment wiring when editing
      is_consignment: productWithCost.is_consignment || false,
      consignment_item_id: productWithCost.consignment_item_id || null,
      consignor_user_id: productWithCost.consignor_user_id || null,
      _consignment_meta: null,
    });
    setNewVariantName('');
    setNewVariantStock('1');
    setNewVariantCost('');
    setShowNewBrandForm(false);
    setProductDialogOpen(true);
  };

  const addVariant = () => {
    if (!newVariantName.trim()) return;
    const variant = {
      id: `v_${Date.now()}`,
      name: newVariantName.trim(),
      stock: parseInt(newVariantStock) || 1,
      cost: newVariantCost !== '' ? parseFloat(newVariantCost) : null,
      price: newVariantPrice !== '' ? parseFloat(newVariantPrice) : null
    };
    setProductForm(p => ({
      ...p,
      variants: [...p.variants, variant]
    }));
    setNewVariantName('');
    setNewVariantStock('1');
    setNewVariantCost('');
    setNewVariantPrice('');
  };

  // Brand management functions
  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;
    try {
      const response = await axios.post(
        `${API}/admin/brands`,
        { name: newBrandName.trim(), color: newBrandColor },
        { headers: getAuthHeader() }
      );
      setBrands(prev => [response.data, ...prev]); // Add to top
      setProductForm(p => ({ ...p, brand: response.data.name }));
      setNewBrandName('');
      setNewBrandColor('#2DD4BF');
      setShowNewBrandForm(false);
      setBrandPopoverOpen(false);
      toast.success(`Brand "${response.data.name}" created!`);
    } catch (error) {
      toast.error(errMsg(error, 'Failed to create brand'));
    }
  };

  const handleUpdateBrand = async (brandId, newName, newColor) => {
    try {
      await axios.put(
        `${API}/admin/brands/${brandId}`,
        { name: newName, color: newColor },
        { headers: getAuthHeader() }
      );
      setBrands(prev => prev.map(b => b.id === brandId ? { ...b, name: newName, color: newColor } : b));
      setEditingBrand(null);
      toast.success('Brand updated!');
    } catch (error) {
      toast.error(errMsg(error, 'Failed to update brand'));
    }
  };

  const handleDeleteBrand = async (brandId, brandName) => {
    if (!window.confirm(`Delete brand "${brandName}"? This won't affect existing products.`)) return;
    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, { headers: getAuthHeader() });
      setBrands(prev => prev.filter(b => b.id !== brandId));
      toast.success('Brand deleted');
    } catch (error) {
      toast.error(errMsg(error, 'Failed to delete brand'));
    }
  };

  const getBrandColor = (brandName) => {
    const brand = brands.find(b => b.name === brandName);
    return brand?.color || '#2DD4BF';
  };

  // LOT management functions
  const handleSetLotOnline = async (lotNumber) => {
    if (!window.confirm(`Set all products in ${lotNumber} to ONLINE? They will be visible in the catalog.`)) return;
    try {
      const response = await axios.put(
        `${API}/admin/lots/${encodeURIComponent(lotNumber)}/online`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(errMsg(error, 'Failed to set LOT online'));
    }
  };

  const handleSetLotOffline = async (lotNumber) => {
    if (!window.confirm(`Set all products in ${lotNumber} to OFFLINE? They will be hidden from the catalog.`)) return;
    try {
      const response = await axios.put(
        `${API}/admin/lots/${encodeURIComponent(lotNumber)}/offline`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(errMsg(error, 'Failed to set LOT offline'));
    }
  };

  const removeVariant = (variantId) => {
    setProductForm(p => ({
      ...p,
      variants: p.variants.filter(v => v.id !== variantId)
    }));
  };

  // Update variant name
  const updateVariantName = (variantId, newName) => {
    setProductForm(p => ({
      ...p,
      variants: p.variants.map(v => 
        v.id === variantId ? { ...v, name: newName } : v
      )
    }));
  };

  // Update variant cost
  const updateVariantCost = (variantId, newCost) => {
    setProductForm(p => ({
      ...p,
      variants: p.variants.map(v => 
        v.id === variantId ? { ...v, cost: newCost === '' ? null : parseFloat(newCost) } : v
      )
    }));
  };

  // Update variant price
  const updateVariantPrice = (variantId, newPrice) => {
    setProductForm(p => ({
      ...p,
      variants: p.variants.map(v => 
        v.id === variantId ? { ...v, price: newPrice === '' ? null : parseFloat(newPrice) } : v
      )
    }));
  };

  // Move variant up or down in the list
  const moveVariant = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= productForm.variants.length) return;
    
    setProductForm(p => {
      const newVariants = [...p.variants];
      const temp = newVariants[index];
      newVariants[index] = newVariants[newIndex];
      newVariants[newIndex] = temp;
      return { ...p, variants: newVariants };
    });
  };

  const updateVariantStock = (variantId, newStock, skipConfirm = false) => {
    const parsedStock = parseInt(newStock) || 0;
    const variant = productForm.variants.find(v => v.id === variantId);
    
    // If editing an existing product and confirmation not skipped, show confirm dialog
    if (editingProduct && !skipConfirm && variant && variant.stock !== parsedStock) {
      setPendingStockChange({
        variantId,
        variantName: variant.name,
        oldStock: variant.stock,
        newStock: parsedStock
      });
      return;
    }
    
    // Apply stock change directly
    setProductForm(p => ({
      ...p,
      variants: p.variants.map(v => 
        v.id === variantId ? { ...v, stock: parsedStock } : v
      )
    }));
  };
  
  const confirmStockChange = () => {
    if (pendingStockChange) {
      setProductForm(p => ({
        ...p,
        variants: p.variants.map(v => 
          v.id === pendingStockChange.variantId ? { ...v, stock: pendingStockChange.newStock } : v
        )
      }));
      setPendingStockChange(null);
    }
  };
  
  const cancelStockChange = () => {
    setPendingStockChange(null);
  };
  
  const incrementVariantStock = (variantId) => {
    const variant = productForm.variants.find(v => v.id === variantId);
    if (variant) {
      updateVariantStock(variantId, variant.stock + 1);
    }
  };
  
  const decrementVariantStock = (variantId) => {
    const variant = productForm.variants.find(v => v.id === variantId);
    if (variant && variant.stock > 0) {
      updateVariantStock(variantId, variant.stock - 1);
    }
  };

  // Update variant image indices (which product images to show for this variant)
  // Supports both legacy single image_index and new image_indices array
  const toggleVariantImageIndex = (variantId, imageIndex) => {
    setProductForm(p => ({
      ...p,
      variants: p.variants.map(v => {
        if (v.id !== variantId) return v;
        
        // Get current indices - migrate from legacy image_index if needed
        let currentIndices = v.image_indices || [];
        if (!Array.isArray(currentIndices) || currentIndices.length === 0) {
          // Migrate from legacy image_index
          if (v.image_index !== undefined && v.image_index !== null) {
            currentIndices = [v.image_index];
          } else {
            currentIndices = [];
          }
        }
        
        // Toggle the image index
        const newIndices = currentIndices.includes(imageIndex)
          ? currentIndices.filter(idx => idx !== imageIndex)
          : [...currentIndices, imageIndex].sort((a, b) => a - b);
        
        return { 
          ...v, 
          image_indices: newIndices,
          // Keep legacy image_index for backwards compatibility (first selected image)
          image_index: newIndices.length > 0 ? newIndices[0] : null
        };
      })
    }));
  };

  // Get selected image indices for a variant (handles legacy and new format)
  const getVariantImageIndices = (variant) => {
    if (variant.image_indices && Array.isArray(variant.image_indices) && variant.image_indices.length > 0) {
      return variant.image_indices;
    }
    if (variant.image_index !== undefined && variant.image_index !== null) {
      return [variant.image_index];
    }
    return [];
  };

  // Direct multi-image upload (cropping removed for speed)
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check max images limit - allow up to 20 images per product
    const currentCount = productForm.images.length;
    const MAX_IMAGES = 20;
    if (currentCount + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed. You can add ${MAX_IMAGES - currentCount} more.`);
      return;
    }
    
    setUploadingImages(true);
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // URLs now come with /api/uploads prefix - store as relative URLs for portability
      // The full URL will be constructed at display time based on current environment
      const newUrls = Array.isArray(response.data?.urls) ? response.data.urls : [];
      const failed = Array.isArray(response.data?.failed) ? response.data.failed : [];
      
      if (newUrls.length > 0) {
        setProductForm(p => ({
          ...p,
          images: [...p.images, ...newUrls],
          // Set first image as cover if no image_url yet
          image_url: p.image_url || newUrls[0]
        }));
      }
      
      if (failed.length > 0) {
        // Surface server-side per-file failures so the admin knows WHICH file
        // failed and WHY (was previously silent — looked like a crash).
        const summary = failed
          .map(f => `${f.filename || 'file'}: ${typeof f.reason === 'string' ? f.reason : 'rejected'}`)
          .join(' · ')
          .slice(0, 280);
        toast.error(`${failed.length} file(s) failed — ${summary}`);
      }
      if (newUrls.length > 0) {
        toast.success(`${newUrls.length} image(s) uploaded`);
      } else if (failed.length === 0) {
        // Neither success nor explicit failure — surface as graceful error,
        // do not let downstream code (e.g. setting cover image) silently break.
        toast.error('No images returned from server');
      }
    } catch (error) {
      toast.error(errMsg(error, 'Failed to upload images'));
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Remove an image
  const removeImage = (indexToRemove) => {
    setProductForm(p => {
      const newImages = p.images.filter((_, i) => i !== indexToRemove);
      // Update cover image if needed
      let newImageUrl = p.image_url;
      if (p.images[indexToRemove] === p.image_url) {
        newImageUrl = newImages[0] || '';
      }
      return { ...p, images: newImages, image_url: newImageUrl };
    });
  };

  // Move image up in order
  const moveImageUp = (index) => {
    if (index === 0) return;
    setProductForm(p => {
      const newImages = [...p.images];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      return { ...p, images: newImages, image_url: newImages[0] };
    });
  };

  // Move image down in order
  const moveImageDown = (index) => {
    if (index === productForm.images.length - 1) return;
    setProductForm(p => {
      const newImages = [...p.images];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      return { ...p, images: newImages, image_url: newImages[0] };
    });
  };

  // Set image as cover (move to first position)
  const setAsCover = (index) => {
    if (index === 0) return;
    setProductForm(p => {
      const newImages = [...p.images];
      // Remove the selected image and add it at the beginning
      const [selectedImage] = newImages.splice(index, 1);
      newImages.unshift(selectedImage);
      return { ...p, images: newImages, image_url: selectedImage };
    });
  };

  // Data Integrity Tools handlers
  const checkBrandIntegrity = async () => {
    setDataToolsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-integrity/brands`, { headers: getAuthHeader() });
      setBrandIntegrityResults(res.data);
    } catch (error) {
      toast.error('Failed to check brand integrity');
    } finally {
      setDataToolsLoading(false);
    }
  };

  const fixBrandCounts = async () => {
    setDataToolsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/data-integrity/fix-brand-counts`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setBrandIntegrityResults(null); // Clear results to re-check
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to fix brand counts');
    } finally {
      setDataToolsLoading(false);
    }
  };

  const checkProductOrdering = async () => {
    setDataToolsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-integrity/product-ordering`, { headers: getAuthHeader() });
      setOrderingResults(res.data);
    } catch (error) {
      toast.error('Failed to check product ordering');
    } finally {
      setDataToolsLoading(false);
    }
  };

  const normalizeProductOrder = async () => {
    setDataToolsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/data-integrity/normalize-product-order`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setOrderingResults(null); // Clear results
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to normalize product ordering');
    } finally {
      setDataToolsLoading(false);
    }
  };

  // Order Brand/Category Fix handlers
  const [brandCategoryError, setBrandCategoryError] = useState(null);
  
  const checkMissingBrandCategory = async () => {
    setDataToolsLoading(true);
    setBrandCategoryError(null);
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) {
        setBrandCategoryError('SESSION EXPIRED - PLEASE LOGIN AGAIN');
        toast.error('SESSION EXPIRED - PLEASE LOGIN AGAIN');
        setDataToolsLoading(false);
        return;
      }
      const res = await axios.get(`${API}/admin/fix-brand-category`, { headers });
      setMissingBrandCategoryData(res.data);
      if (res.data.total_missing === 0) {
        toast.success('NO ITEMS WITH MISSING BRAND/CATEGORY');
      } else {
        toast.info(`FOUND ${res.data.total_missing} ITEMS WITH MISSING DATA`);
      }
    } catch (error) {
      const msg = errMsg(error, 'UNKNOWN ERROR');
      setBrandCategoryError(`ERROR: ${msg}`);
      toast.error(msg);
    } finally {
      setDataToolsLoading(false);
    }
  };

  const fixSingleItem = async (orderId, itemIndex, brand, category) => {
    const key = `${orderId}-${itemIndex}`;
    setFixingItems(prev => ({ ...prev, [key]: true }));
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) {
        toast.error('SESSION EXPIRED - PLEASE LOGIN AGAIN');
        return;
      }
      const params = new URLSearchParams();
      if (brand) params.append('brand', brand);
      if (category) params.append('category', category);
      
      await axios.put(
        `${API}/admin/fix-brand-category/${orderId}/${itemIndex}?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('ITEM UPDATED');
      // Refresh the list
      checkMissingBrandCategory();
    } catch (error) {
      toast.error('FAILED TO UPDATE ITEM');
    } finally {
      setFixingItems(prev => ({ ...prev, [key]: false }));
    }
  };

  const autoFixFromProducts = async () => {
    setDataToolsLoading(true);
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) {
        toast.error('SESSION EXPIRED - PLEASE LOGIN AGAIN');
        setDataToolsLoading(false);
        return;
      }
      const res = await axios.post(`${API}/admin/auto-fix-brand-category`, {}, { headers });
      if (res.data.total_fixed > 0) {
        toast.success(`AUTO-FIXED ${res.data.total_fixed} ITEMS`);
        checkMissingBrandCategory(); // Refresh list
      } else {
        toast.info('NO ITEMS COULD BE AUTO-FIXED (PRODUCTS MAY BE DELETED)');
      }
    } catch (error) {
      console.error('Auto-fix error:', error.response || error);
      toast.error(errMsg(error, 'AUTO-FIX FAILED'));
    } finally {
      setDataToolsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING ADMIN</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <AdminNotesBulkBar />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <AdminNotifications />
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="font-sans text-3xl sm:text-4xl text-white tracking-tighter" data-testid="admin-title">
            ADMIN DASHBOARD
          </h1>
          <div className="flex gap-2 flex-shrink-0">
            <Dialog open={dataToolsOpen} onOpenChange={setDataToolsOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-zinc-800 text-zinc-400 hover:bg-zinc-800 rounded-none text-xs sm:text-sm"
                  data-testid="data-tools-btn"
                >
                  <Wrench className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">DATA TOOLS</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-zinc-800 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-mono text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-tiffany-500" />
                    DATA INTEGRITY TOOLS
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 pt-4">
                  {/* Brand Counts Section */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">BRAND COUNTS</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      Check and fix product count discrepancies in brand labels.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Button
                        onClick={checkBrandIntegrity}
                        disabled={dataToolsLoading}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs"
                      >
                        CHECK ISSUES
                      </Button>
                      <Button
                        onClick={fixBrandCounts}
                        disabled={dataToolsLoading}
                        size="sm"
                        className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-xs"
                      >
                        FIX COUNTS
                      </Button>
                    </div>
                    {brandIntegrityResults && (
                      <div className="bg-zinc-900 p-3 text-xs font-mono">
                        <p className="text-zinc-400">Total brands: {brandIntegrityResults.total_brands}</p>
                        <p className={brandIntegrityResults.issues_found > 0 ? 'text-red-500' : 'text-green-500'}>
                          Issues found: {brandIntegrityResults.issues_found}
                        </p>
                        {brandIntegrityResults.issues?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {brandIntegrityResults.issues.map((issue, i) => (
                              <p key={i} className="text-yellow-500">
                                {issue.brand}: shows {issue.stored_count}, actual {issue.actual_total}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Product Ordering Section */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">PRODUCT ORDERING</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      Check for ordering issues and normalize product display order.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Button
                        onClick={checkProductOrdering}
                        disabled={dataToolsLoading}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs"
                      >
                        CHECK ISSUES
                      </Button>
                      <Button
                        onClick={normalizeProductOrder}
                        disabled={dataToolsLoading}
                        size="sm"
                        className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-xs"
                      >
                        NORMALIZE ORDER
                      </Button>
                    </div>
                    {orderingResults && (
                      <div className="bg-zinc-900 p-3 text-xs font-mono">
                        <p className={orderingResults.missing_display_order?.length > 0 ? 'text-yellow-500' : 'text-green-500'}>
                          Missing order: {orderingResults.missing_display_order?.length || 0} products
                        </p>
                        <p className={orderingResults.duplicate_orders?.length > 0 ? 'text-yellow-500' : 'text-green-500'}>
                          Duplicate orders: {orderingResults.duplicate_orders?.length || 0}
                        </p>
                        {orderingResults.duplicate_orders?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {orderingResults.duplicate_orders.slice(0, 5).map((dup, i) => (
                              <p key={i} className="text-zinc-400">
                                {dup.brand || 'No brand'}: order #{dup.display_order} has {dup.count} products
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Email Settings Section */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">EMAIL NOTIFICATIONS</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      Control which emails are sent from the website. Disable if using Pirateship emails.
                    </p>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-400">SHIPPING EMAILS</span>
                        {emailSettings.shipping_email_enabled ? (
                          <span className="font-mono text-[10px] text-green-500 bg-green-500/20 px-2 py-0.5">ENABLED</span>
                        ) : (
                          <span className="font-mono text-[10px] text-red-500 bg-red-500/20 px-2 py-0.5">DISABLED</span>
                        )}
                      </div>
                      <Switch
                        checked={emailSettings.shipping_email_enabled}
                        onCheckedChange={toggleShippingEmail}
                        disabled={emailSettingsLoading}
                      />
                    </div>
                    <Button
                      onClick={sendTestShippingEmail}
                      disabled={emailSettingsLoading}
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs w-full"
                    >
                      {emailSettingsLoading ? 'SENDING...' : 'SEND TEST SHIPPING EMAIL'}
                    </Button>
                  </div>

                  {/* Resend Payment Confirmation Email */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">RESEND PAYMENT CONFIRMATION</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      RESEND PAYMENT CONFIRMATION EMAIL FOR A SPECIFIC ORDER.
                    </p>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="ORDER ID (E.G. BLK-059392)"
                        value={resendOrderId}
                        onChange={(e) => setResendOrderId(e.target.value.toUpperCase())}
                        className="flex-1 bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-xs font-mono"
                      />
                      <Button
                        onClick={handleResendPaymentConfirmation}
                        disabled={resendLoading}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs whitespace-nowrap"
                      >
                        {resendLoading ? 'SENDING...' : 'RESEND'}
                      </Button>
                    </div>
                    {resendResult && (
                      <p className={`font-mono text-xs ${resendResult.success ? 'text-green-500' : 'text-red-500'}`}>
                        {resendResult.message}
                      </p>
                    )}
                  </div>

                  {/* Order Brand/Category Fix Section */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">ORDER BRAND/CATEGORY FIX</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      FIND AND FIX ORDERS WITH MISSING BRAND OR CATEGORY DATA FOR ACCURATE ANALYTICS.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Button
                        onClick={checkMissingBrandCategory}
                        disabled={dataToolsLoading}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs"
                      >
                        FIND ISSUES
                      </Button>
                      <Button
                        onClick={autoFixFromProducts}
                        disabled={dataToolsLoading}
                        size="sm"
                        className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-xs"
                      >
                        AUTO-FIX
                      </Button>
                    </div>
                    
                    {brandCategoryError && (
                      <div className="bg-red-500/20 border border-red-500 p-2 mb-3 text-red-400 text-[10px] font-mono">
                        {brandCategoryError}
                      </div>
                    )}
                    
                    {missingBrandCategoryData && (
                      <div className="bg-zinc-900 p-2 text-xs font-mono">
                        <p className={missingBrandCategoryData.total_missing > 0 ? 'text-yellow-500 mb-2' : 'text-green-500'}>
                          {missingBrandCategoryData.total_missing} ITEMS NEED FIX
                        </p>
                        
                        {missingBrandCategoryData.items?.length > 0 && (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {missingBrandCategoryData.items.map((item, idx) => (
                              <div key={idx} className="border border-zinc-700 p-2">
                                <p className="text-white text-[10px] truncate">{item.product_name}</p>
                                <p className="text-zinc-500 text-[9px] mb-2">{item.order_id}</p>
                                <div className="grid grid-cols-2 gap-1 mb-1">
                                  <select
                                    className="w-full bg-zinc-800 border border-zinc-700 text-white px-1 py-1 text-[9px] rounded-none"
                                    defaultValue={item.current_brand || item.suggested_brand || ''}
                                    onChange={(e) => {
                                      e.target.dataset.brand = e.target.value;
                                    }}
                                  >
                                    <option value="">BRAND</option>
                                    {missingBrandCategoryData.available_brands?.map(b => (
                                      <option key={b} value={b}>{b.toUpperCase()}</option>
                                    ))}
                                  </select>
                                  <select
                                    className="w-full bg-zinc-800 border border-zinc-700 text-white px-1 py-1 text-[9px] rounded-none"
                                    defaultValue={item.current_category || item.suggested_category || ''}
                                    onChange={(e) => {
                                      e.target.dataset.category = e.target.value;
                                    }}
                                  >
                                    <option value="">CATEGORY</option>
                                    {missingBrandCategoryData.available_categories?.map(c => (
                                      <option key={c} value={c}>{c.toUpperCase()}</option>
                                    ))}
                                  </select>
                                </div>
                                <Button
                                  onClick={(e) => {
                                    const container = e.target.closest('.border-zinc-700');
                                    const brandSelect = container.querySelector('select:first-of-type');
                                    const catSelect = container.querySelector('select:last-of-type');
                                    fixSingleItem(item.order_id, item.item_index, brandSelect.value, catSelect.value);
                                  }}
                                  disabled={fixingItems[`${item.order_id}-${item.item_index}`]}
                                  size="sm"
                                  className="w-full bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-[9px] py-1"
                                >
                                  {fixingItems[`${item.order_id}-${item.item_index}`] ? 'FIXING...' : 'FIX ITEM'}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Fix Missing Item Costs Section */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">FIX MISSING ITEM COSTS</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      VIEW AND FIX ORDER ITEMS MISSING COST DATA FOR ACCURATE PROFIT TRACKING.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Button
                        onClick={async () => {
                          try {
                            setDataToolsLoading(true);
                            const res = await axios.get(`${API}/admin/items-missing-cost`, { headers: getAuthHeader() });
                            setMissingCostItems(res.data);
                          } catch (err) {
                            toast.error(errMsg(err, 'Failed to load items'));
                          } finally {
                            setDataToolsLoading(false);
                          }
                        }}
                        disabled={dataToolsLoading}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs"
                      >
                        FIND ITEMS
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/admin/auto-fix-item-costs`, {}, { headers: getAuthHeader() });
                            toast.success(`Fixed ${res.data.fixed_count} items. ${res.data.not_fixed_count} could not be fixed.`);
                            setMissingCostItems(null);
                            fetchData();
                          } catch (err) {
                            toast.error(errMsg(err, 'Failed to fix costs'));
                          }
                        }}
                        disabled={dataToolsLoading}
                        size="sm"
                        className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-xs"
                      >
                        AUTO-FIX ALL
                      </Button>
                    </div>
                    
                    {missingCostItems && (
                      <div className="bg-zinc-900 p-2 text-xs font-mono">
                        <p className={missingCostItems.total_missing > 0 ? 'text-yellow-500 mb-2' : 'text-green-500'}>
                          {missingCostItems.total_missing} ITEMS MISSING COST
                        </p>
                        
                        {missingCostItems.items?.length > 0 && (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {missingCostItems.items.map((item, idx) => (
                              <div key={idx} className="border border-zinc-700 p-2">
                                <div className="flex justify-between items-start mb-1">
                                  <span className="text-zinc-300">{item.product_name}</span>
                                  <span className="text-zinc-500 text-[10px]">{item.order_number}</span>
                                </div>
                                <div className="text-[10px] text-zinc-500 mb-2">
                                  Sold: ${item.sale_price?.toFixed(2)} | Suggested: {item.suggested_cost ? `$${item.suggested_cost.toFixed(2)}` : 'N/A'}
                                </div>
                                <div className="flex gap-2 items-center">
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Cost"
                                    defaultValue={item.suggested_cost || ''}
                                    className="w-20 bg-zinc-800 border border-zinc-700 text-white px-2 py-1 text-[10px] rounded-none"
                                    id={`cost-input-${idx}`}
                                  />
                                  <Button
                                    onClick={async () => {
                                      const costInput = document.getElementById(`cost-input-${idx}`);
                                      const cost = parseFloat(costInput.value);
                                      if (isNaN(cost) || cost < 0) {
                                        toast.error('Enter a valid cost');
                                        return;
                                      }
                                      try {
                                        await axios.put(
                                          `${API}/admin/fix-item-cost/${item.order_id}/${item.item_index}?cost=${cost}`,
                                          {},
                                          { headers: getAuthHeader() }
                                        );
                                        toast.success(`Fixed cost for ${item.product_name}`);
                                        // Refresh the list
                                        const res = await axios.get(`${API}/admin/items-missing-cost`, { headers: getAuthHeader() });
                                        setMissingCostItems(res.data);
                                      } catch (err) {
                                        toast.error(errMsg(err, 'Failed to fix cost'));
                                      }
                                    }}
                                    size="sm"
                                    className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-[9px] py-1 h-6"
                                  >
                                    FIX
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Edit Recent Order Costs */}
                  <div className="border border-zinc-800 p-4">
                    <h3 className="font-mono text-sm text-white mb-3">EDIT ORDER COSTS</h3>
                    <p className="text-zinc-500 text-xs mb-3">
                      SEARCH FOR AN ORDER OR VIEW ITEMS STILL MISSING COST.
                    </p>
                    
                    {/* Search by order number */}
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        placeholder="Order # (e.g. BLK-000123)"
                        className="flex-1 bg-zinc-800 border border-zinc-700 text-white px-2 py-1 text-xs rounded-none"
                        id="order-search-input"
                      />
                      <Button
                        onClick={async () => {
                          const orderNum = document.getElementById('order-search-input').value.trim();
                          if (!orderNum) {
                            toast.error('Enter an order number');
                            return;
                          }
                          try {
                            setDataToolsLoading(true);
                            const res = await axios.get(`${API}/admin/order-item-costs/${orderNum}`, { headers: getAuthHeader() });
                            setRecentOrderCosts([res.data]);
                          } catch (err) {
                            toast.error(errMsg(err, 'Order not found'));
                          } finally {
                            setDataToolsLoading(false);
                          }
                        }}
                        disabled={dataToolsLoading}
                        size="sm"
                        className="bg-tiffany-500 text-black hover:bg-tiffany-600 rounded-none text-xs"
                      >
                        FIND
                      </Button>
                    </div>
                    
                    {recentOrderCosts && recentOrderCosts.length > 0 && (
                      <div className="bg-zinc-900 p-2 text-xs font-mono mt-3 max-h-80 overflow-y-auto">
                        {recentOrderCosts.map((order, orderIdx) => (
                          <div key={order.order_id || orderIdx} className="border border-zinc-700 p-2 mb-2">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-tiffany-400">{order.order_number}</span>
                              <span className="text-zinc-500 text-[10px]">{order.order_date}</span>
                            </div>
                            {order.items.map((item, itemIdx) => (
                              <div key={`${order.order_id}-${item.item_index}`} className="flex items-center justify-between gap-2 py-1 border-t border-zinc-800">
                                <span className="text-zinc-300 text-[10px] flex-1 truncate">{item.product_name}</span>
                                <span className="text-zinc-500 text-[10px]">${item.sale_price?.toFixed(2)}</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Cost"
                                  key={`input-${order.order_id}-${item.item_index}-${item.current_cost}`}
                                  defaultValue={item.current_cost !== null && item.current_cost !== undefined ? item.current_cost : ''}
                                  className="w-16 bg-zinc-800 border border-zinc-700 text-white px-1 py-0.5 text-[10px] rounded-none"
                                  id={`order-cost-${order.order_id}-${item.item_index}`}
                                />
                                <Button
                                  onClick={async () => {
                                    const costInput = document.getElementById(`order-cost-${order.order_id}-${item.item_index}`);
                                    const cost = parseFloat(costInput.value);
                                    if (isNaN(cost) || cost < 0) {
                                      toast.error('Enter a valid cost');
                                      return;
                                    }
                                    try {
                                      await axios.put(
                                        `${API}/admin/fix-item-cost/${order.order_id}/${item.item_index}?cost=${cost}`,
                                        {},
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success(`Updated cost for ${item.product_name}`);
                                    } catch (err) {
                                      toast.error(errMsg(err, 'Failed to update cost'));
                                    }
                                  }}
                                  size="sm"
                                  className="bg-zinc-700 text-white hover:bg-zinc-600 rounded-none text-[9px] py-0.5 h-5 px-2"
                                >
                                  SAVE
                                </Button>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {dataToolsLoading && (
                    <div className="text-center text-tiffany-500 font-mono text-sm">
                      Processing...
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-zinc-800 text-zinc-400 hover:bg-zinc-800 rounded-none text-xs sm:text-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">REFRESH</span>
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none p-1.5 grid grid-cols-3 sm:grid-cols-6 w-full max-w-4xl h-auto sm:h-12 gap-1">
            <TabsTrigger value="drop" className="h-9 sm:h-full rounded-none data-[state=active]:bg-tiffany-500 data-[state=active]:text-black font-mono text-[10px] sm:text-sm px-1 sm:px-2">
              DROP
            </TabsTrigger>
            <TabsTrigger value="overview" className="h-9 sm:h-full rounded-none data-[state=active]:bg-tiffany-500 data-[state=active]:text-black font-mono text-[10px] sm:text-sm px-1 sm:px-2">
              OVERVIEW
            </TabsTrigger>
            <TabsTrigger value="products" className="h-9 sm:h-full rounded-none data-[state=active]:bg-tiffany-500 data-[state=active]:text-black font-mono text-[10px] sm:text-sm px-1 sm:px-2">
              PRODUCTS
            </TabsTrigger>
            <TabsTrigger value="offers" className="h-9 sm:h-full rounded-none data-[state=active]:bg-yellow-500 data-[state=active]:text-black font-mono text-[10px] sm:text-sm px-1 sm:px-2 relative">
              OFFERS
              {(offerCounts.pending > 0 || offerCounts.user_final > 0) && (
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {(offerCounts.pending || 0) + (offerCounts.user_final || 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="plans" className="h-9 sm:h-full rounded-none data-[state=active]:bg-purple-500 data-[state=active]:text-black font-mono text-[10px] sm:text-sm px-1 sm:px-2 relative">
              PLANS
              {(paymentPlanCounts.pending > 0 || paymentPlanCounts.active > 0) && (
                <span className="absolute -top-1 -right-1 bg-purple-500 text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {(paymentPlanCounts.pending || 0) + (paymentPlanCounts.active || 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="orders" className="h-9 sm:h-full rounded-none data-[state=active]:bg-blue-500 data-[state=active]:text-white font-mono text-[10px] sm:text-sm px-1 sm:px-2 relative">
              ORDERS
              {orders.filter(o => o.status === 'pending').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {orders.filter(o => o.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <CrashUptimeWidget />
              <InboundWidget />
              <ConsignmentWidget />
            </div>
            {prefsLoaded ? (
              <DraggableToolGrid
                stats={stats}
                unreadMessages={unreadMessages}
                onTabChange={(tab, filter) => {
                  setActiveTab(tab);
                  // If filter is provided, set the cost filter
                  if (filter === 'missing') {
                    setProductCostFilter('missing');
                  }
                }}
                savedOrder={dashboardToolOrder}
                onOrderChange={handleToolOrderChange}
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 animate-pulse">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="bg-zinc-800/50 h-24 rounded"></div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-2">
            {/* Products Header - Compact */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-sans text-lg text-white">PRODUCTS ({products.length})</h2>
                <div className="flex gap-2 font-mono text-[10px] flex-wrap">
                  <span className="text-green-500">{products.filter(p => p.visibility === 'online' || (p.visibility === undefined && p.is_online !== false)).length} ON</span>
                  <span className="text-yellow-500">{products.filter(p => p.visibility === 'coming_soon').length} SOON</span>
                  <span className="text-zinc-500">{products.filter(p => p.visibility === 'offline' || (p.visibility === undefined && p.is_online === false)).length} OFF</span>
                  {/* Missing cost count - exclude gift cards and BLKCRD */}
                  {(() => {
                    const isValidCost = (cost) => {
                      if (cost === null || cost === undefined || cost === '') return false;
                      if (typeof cost === 'number') return true;
                      if (typeof cost === 'string' && cost.trim() !== '' && !isNaN(parseFloat(cost))) return true;
                      return false;
                    };
                    const missingCost = products.filter(p => {
                      if (p.is_gift_card || p.is_blkcrd) return false;
                      // For variant products, check if any variant missing cost
                      if (p.has_variants && p.variants?.length > 0) {
                        return p.variants.some(v => !isValidCost(v.cost));
                      }
                      // For non-variant products
                      return !isValidCost(p.cost);
                    }).length;
                    return missingCost > 0 ? (
                      <span className="text-red-500 cursor-pointer hover:underline uppercase" onClick={() => setProductCostFilter('missing')}>
                        NO COST:{missingCost}
                      </span>
                    ) : null;
                  })()}
                  {lots.map(lot => (
                    <span key={lot.lot_number} className={lot.is_fully_online ? 'text-tiffany-500' : 'text-zinc-600'}>
                      {lot.lot_number}:{lot.total_items}
                      {lot.true_total !== lot.total_items && (
                        <span className="text-yellow-500 ml-0.5">({lot.true_total})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Add Product Button */}
                <Dialog open={productDialogOpen} onOpenChange={(open) => {
                  // If trying to close and editing an existing product, check for cost
                  // Skip cost requirement for gift cards and BLKCRD
                  const isDigitalProduct = productForm.is_gift_card || productForm.is_blkcrd;
                  if (!open && editingProduct && !isDigitalProduct && (!productForm.cost && productForm.cost !== 0 && productForm.cost !== '0')) {
                    toast.error('COST is required before closing. Enter what you paid for this item.');
                    return; // Prevent close
                  }
                  setProductDialogOpen(open);
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={resetProductForm}
                      className="border-tiffany-500 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none text-[10px] sm:text-sm px-4 sm:px-6"
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      ADD PRODUCT
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-[#0a0a0a] border-zinc-800 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-sans text-xl text-white">
                      {editingProduct ? 'EDIT PRODUCT' : 'ADD PRODUCT'}
                      {productForm.is_consignment && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 font-mono text-[10px] tracking-widest align-middle">
                          CONSIGNMENT
                        </span>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  {/* Consignment context banner — shows seller info + floor so admin
                      can verify cost/price before saving. Tagged data-testid for E2E. */}
                  {productForm.is_consignment && productForm._consignment_meta && (
                    <div className="bg-purple-500/10 border border-purple-500/40 p-3 mt-3 font-mono text-[11px] space-y-1" data-testid="consignment-prefill-banner">
                      <p className="text-purple-300 tracking-widest font-bold flex items-center gap-2">
                        <Handshake size={14} /> CONSIGNMENT LISTING
                      </p>
                      <p className="text-zinc-300">
                        SELLER: <span className="text-white">{productForm._consignment_meta.seller_name?.toUpperCase() || productForm._consignment_meta.seller_email?.toUpperCase()}</span>
                      </p>
                      <p className="text-zinc-400">
                        FLOOR <span className="text-white">${Number(productForm._consignment_meta.base_floor || 0).toFixed(2)}</span>
                        {' · '}COMMISSION <span className="text-white">{productForm._consignment_meta.commission_pct ?? 30}%</span>
                        {' · '}SUGGESTED <span className="text-white">${Number(productForm._consignment_meta.suggested_price || 0).toFixed(2)}</span>
                      </p>
                      <p className="text-zinc-500 text-[10px]">
                        COST field below = what you owe the seller at minimum. The sale-hook auto-credits {productForm._consignment_meta.seller_name?.split(' ')[0] || 'the seller'}'s wallet when this sells.
                      </p>
                    </div>
                  )}
                  <form onSubmit={handleProductSubmit} className="space-y-3 mt-4">
                    {/* Quick toggles at top - side by side */}
                    <div className="grid grid-cols-2 gap-2">
                      {/* Visibility Status Selector */}
                      <div className="border border-zinc-800 p-2">
                        <Select
                          value={productForm.visibility}
                          onValueChange={(v) => setProductForm(p => ({ ...p, visibility: v }))}
                        >
                          <SelectTrigger className="bg-zinc-900 border-zinc-700 h-8 font-mono text-xs w-full">
                            <SelectValue placeholder="STATUS" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700">
                            <SelectItem value="offline" className="font-mono text-xs">
                              <span className="text-zinc-400">⚫ OFFLINE</span>
                            </SelectItem>
                            <SelectItem value="coming_soon" className="font-mono text-xs">
                              <span className="text-yellow-400">🟡 COMING SOON</span>
                            </SelectItem>
                            <SelectItem value="online" className="font-mono text-xs">
                              <span className="text-green-400">🟢 ONLINE</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* One Time Item Toggle */}
                      {!productForm.is_bidding && (
                        <div className="flex items-center justify-between border border-zinc-800 p-2">
                          <span className="font-mono text-xs text-white">ONE TIME</span>
                          <Switch
                            checked={productForm.is_one_time}
                            onCheckedChange={(v) => setProductForm(p => ({ ...p, is_one_time: v }))}
                            data-testid="product-one-time-switch"
                          />
                        </div>
                      )}
                      {productForm.is_bidding && (
                        <div className="flex items-center justify-between border border-zinc-800 p-2">
                          <span className="font-mono text-xs text-white">AUCTION</span>
                          <Switch checked={true} disabled className="opacity-50" />
                        </div>
                      )}
                    </div>

                    {/* LOT Number - compact */}
                    <div className="flex items-center gap-2 border border-zinc-800 p-2">
                      <span className="font-mono text-xs text-zinc-400 whitespace-nowrap">LOT:</span>
                      <Select 
                        value={productForm.lot_type === 'custom' ? 'custom' : (productForm.lot_number || 'none')}
                        onValueChange={(v) => {
                          if (v === 'custom') {
                            setProductForm(p => ({ ...p, lot_type: 'custom', lot_number: '' }));
                          } else if (v === 'none') {
                            setProductForm(p => ({ ...p, lot_type: 'preset', lot_number: '' }));
                          } else {
                            setProductForm(p => ({ ...p, lot_type: 'preset', lot_number: v }));
                          }
                        }}
                      >
                        <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 text-xs flex-1" data-testid="lot-type-select">
                          <SelectValue placeholder="None">{productForm.lot_type === 'custom' ? 'Custom' : (productForm.lot_number || 'None')}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                          <SelectItem value="none" className="text-zinc-400 text-xs">None</SelectItem>
                          <SelectItem value="LOT 1" className="text-white text-xs">LOT 1</SelectItem>
                          <SelectItem value="LOT 2" className="text-white text-xs">LOT 2</SelectItem>
                          <SelectItem value="LOT 3" className="text-white text-xs">LOT 3</SelectItem>
                          <SelectItem value="custom" className="text-tiffany-500 text-xs">Custom...</SelectItem>
                        </SelectContent>
                      </Select>
                      {productForm.lot_type === 'custom' && (
                        <Input
                          placeholder="Custom LOT"
                          value={productForm.lot_number}
                          onChange={(e) => {
                            const input = e.target;
                            const start = input.selectionStart;
                            const end = input.selectionEnd;
                            setProductForm(p => ({ ...p, lot_number: e.target.value.toUpperCase() }));
                            requestAnimationFrame(() => input.setSelectionRange(start, end));
                          }}
                          className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 text-xs flex-1"
                        />
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-mono text-zinc-400 mb-1 block">NAME *</label>
                      <Input
                        placeholder="PRODUCT NAME..."
                        value={productForm.name}
                        onChange={(e) => {
                          const input = e.target;
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          const upperValue = e.target.value.toUpperCase();
                          setProductForm(p => ({ ...p, name: upperValue }));
                          // Restore cursor position after React re-render
                          requestAnimationFrame(() => {
                            input.setSelectionRange(start, end);
                          });
                        }}
                        className="bg-zinc-900 border-zinc-800 text-white rounded-none uppercase"
                        required
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-mono text-zinc-400 mb-1 block">DESCRIPTION *</label>
                      <Textarea
                        placeholder="PRODUCT DESCRIPTION..."
                        value={productForm.description}
                        onChange={(e) => {
                          const input = e.target;
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          const upperValue = e.target.value.toUpperCase();
                          setProductForm(p => ({ ...p, description: upperValue }));
                          // Restore cursor position after React re-render
                          requestAnimationFrame(() => {
                            input.setSelectionRange(start, end);
                          });
                        }}
                        className="bg-zinc-900 border-zinc-800 text-white rounded-none min-h-16 uppercase"
                        required
                        data-testid="product-description-input"
                      />
                    </div>
                    <div className={`grid gap-4 ${productForm.is_bidding ? 'grid-cols-1' : (productForm.is_gift_card || productForm.is_blkcrd ? 'grid-cols-2' : 'grid-cols-3')}`}>
                      {/* Hide price field when auction is enabled - auction uses Starting Bid instead */}
                      {!productForm.is_bidding && (
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">PRICE ($) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={productForm.price}
                            onChange={(e) => setProductForm(p => ({ ...p, price: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                            required
                            data-testid="product-price-input"
                          />
                        </div>
                      )}
                      {/* COST - what admin paid for the item (admin-only, skip for gift cards/BLKCRD) */}
                      {!productForm.is_gift_card && !productForm.is_blkcrd && (
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">COST ($) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={productForm.cost}
                            onChange={(e) => setProductForm(p => ({ ...p, cost: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                            required
                            data-testid="product-cost-input"
                          />
                          {productForm.price && productForm.cost && (
                            <p className={`text-xs mt-1 font-mono ${parseFloat(productForm.price) - parseFloat(productForm.cost) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              PROFIT: ${(parseFloat(productForm.price) - parseFloat(productForm.cost)).toFixed(2)}
                            </p>
                          )}
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-mono text-zinc-400 mb-1 block">CATEGORY *</label>
                        <Select 
                          value={productForm.category} 
                          onValueChange={(v) => setProductForm(p => ({ ...p, category: v }))}
                        >
                          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white rounded-none uppercase" data-testid="product-category-select">
                            <SelectValue placeholder="SELECT CATEGORY" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none z-[100]">
                            {(adminCategories.length > 0 ? adminCategories : CATEGORIES.map(c => ({name: c.toUpperCase()}))).map(cat => (
                              <SelectItem key={cat.name || cat} value={cat.name || cat} className="text-white uppercase">{(cat.name || cat).toUpperCase()}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Brand Selector */}
                    <div>
                      <label className="text-xs font-mono text-zinc-400 mb-1 block flex items-center gap-2">
                        <Tag className="w-3 h-3" />
                        BRAND *
                      </label>
                      <Popover open={brandPopoverOpen} onOpenChange={setBrandPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={brandPopoverOpen}
                            className="w-full justify-between bg-zinc-900 border-zinc-800 text-white rounded-none hover:bg-zinc-800 hover:text-white h-10"
                            data-testid="brand-selector"
                          >
                            {productForm.brand ? (
                              <span className="flex items-center gap-2 uppercase">
                                <span 
                                  className="w-3 h-3 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: getBrandColor(productForm.brand) }}
                                />
                                {productForm.brand.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-zinc-500">SELECT BRAND *</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent 
                          className="w-[280px] p-0 bg-zinc-900 border-zinc-800 rounded-none max-h-[250px] overflow-hidden z-[200]" 
                          align="start" 
                          side="bottom"
                          sideOffset={4}
                          collisionPadding={10}
                        >
                          <Command className="bg-zinc-900 [&_[cmdk-input]]:uppercase">
                            <CommandInput 
                              placeholder="SEARCH BRANDS..." 
                              className="h-9 text-white"
                            />
                            <CommandList className="max-h-[240px] overflow-y-auto">
                              <CommandEmpty className="py-2 text-center text-zinc-500">
                                {showNewBrandForm ? (
                                  <div className="px-3 py-2 space-y-3">
                                    <div>
                                      <label className="text-xs text-zinc-400 block mb-1 text-left">BRAND NAME</label>
                                      <Input
                                        value={newBrandName}
                                        onChange={(e) => {
                                          const input = e.target;
                                          const start = input.selectionStart;
                                          const end = input.selectionEnd;
                                          setNewBrandName(e.target.value.toUpperCase());
                                          requestAnimationFrame(() => input.setSelectionRange(start, end));
                                        }}
                                        placeholder="ENTER BRAND NAME"
                                        className="bg-zinc-800 border-zinc-700 text-white rounded-none h-8 uppercase"
                                        autoFocus
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-zinc-400 block mb-1 text-left">LABEL COLOR</label>
                                      <div className="flex gap-2 items-center">
                                        <input
                                          type="color"
                                          value={newBrandColor}
                                          onChange={(e) => setNewBrandColor(e.target.value)}
                                          className="w-8 h-8 cursor-pointer bg-transparent border-0"
                                        />
                                        <Input
                                          value={newBrandColor}
                                          onChange={(e) => setNewBrandColor(e.target.value)}
                                          placeholder="#2DD4BF"
                                          className="bg-zinc-800 border-zinc-700 text-white rounded-none h-8 flex-1"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => setShowNewBrandForm(false)}
                                        className="flex-1 bg-zinc-700 hover:bg-zinc-600 rounded-none h-8"
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleCreateBrand}
                                        disabled={!newBrandName.trim()}
                                        className="flex-1 bg-tiffany-500 hover:bg-tiffany-400 text-black rounded-none h-8"
                                      >
                                        Create
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowNewBrandForm(true);
                                    }}
                                    className="text-tiffany-500 hover:underline uppercase font-mono cursor-pointer"
                                  >
                                    + ADD NEW BRAND
                                  </button>
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {/* Clear selection option */}
                                {productForm.brand && (
                                  <CommandItem
                                    value="__clear__"
                                    onSelect={() => {
                                      setProductForm(p => ({ ...p, brand: '' }));
                                      setBrandPopoverOpen(false);
                                    }}
                                    className="text-zinc-400 hover:text-white cursor-pointer"
                                  >
                                    <X className="mr-2 h-4 w-4" />
                                    Clear selection
                                  </CommandItem>
                                )}
                                {brands.map((brand) => (
                                  <CommandItem
                                    key={brand.id}
                                    value={brand.name}
                                    onSelect={(currentValue) => {
                                      setProductForm(p => ({ ...p, brand: currentValue }));
                                      setBrandPopoverOpen(false);
                                    }}
                                    className="text-white cursor-pointer uppercase"
                                  >
                                    <span 
                                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                                      style={{ backgroundColor: brand.color }}
                                    />
                                    {brand.name.toUpperCase()}
                                    {brand.usage_count > 0 && (
                                      <span className="ml-auto text-xs text-zinc-500">
                                        {brand.usage_count}
                                      </span>
                                    )}
                                    <Check
                                      className={`ml-2 h-4 w-4 ${productForm.brand === brand.name ? 'opacity-100' : 'opacity-0'}`}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                              {brands.length > 0 && (
                                <div className="p-2 border-t border-zinc-800">
                                  <button
                                    type="button"
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowNewBrandForm(true);
                                    }}
                                    className="text-xs text-tiffany-500 hover:text-tiffany-400 w-full text-center uppercase font-mono cursor-pointer"
                                  >
                                    + ADD NEW BRAND
                                  </button>
                                </div>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {/* Product Images Upload */}
                    <div className="space-y-3">
                      <label className="text-xs font-mono text-zinc-400 mb-1 block flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        PRODUCT IMAGES (up to 5)
                      </label>
                      
                      {/* Uploaded Images Preview */}
                      {productForm.images.length > 0 && (
                        <div className="grid grid-cols-5 gap-2 mb-3">
                          {productForm.images.map((url, index) => (
                            <div 
                              key={index} 
                              className={`relative group aspect-square bg-zinc-900 border-2 cursor-pointer transition-all ${
                                index === 0 
                                  ? 'border-tiffany-500' 
                                  : 'border-zinc-800 hover:border-tiffany-500/50'
                              }`}
                              onClick={() => {
                                if (index > 0) setAsCover(index);
                              }}
                            >
                              <img 
                                src={thumbUrl(url, 'medium')} 
                                alt={`Product ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                              {/* Cover badge */}
                              {index === 0 && (
                                <div className="absolute top-1 left-1 bg-tiffany-500 text-black text-[8px] px-1 font-mono font-bold">
                                  COVER
                                </div>
                              )}
                              {/* Click to set cover hint (non-cover images only) */}
                              {index > 0 && (
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                                  <span className="opacity-0 group-hover:opacity-100 text-white text-[10px] font-mono bg-black/60 px-2 py-1 transition-opacity">
                                    SET AS COVER
                                  </span>
                                </div>
                              )}
                              {/* Delete button */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index);
                                }}
                                className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload Button - Direct upload (no cropping) */}
                      {productForm.images.length < 20 && (
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            data-testid="product-image-upload"
                          />
                          <div className={`border-2 border-dashed border-zinc-700 hover:border-tiffany-500/50 p-6 text-center transition-colors ${uploadingImages ? 'opacity-50' : ''}`}>
                            <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
                            <p className="font-mono text-sm text-zinc-400">
                              {uploadingImages ? 'UPLOADING...' : 'CLICK TO ADD IMAGES'}
                            </p>
                            <p className="font-mono text-xs text-zinc-600 mt-1">
                              {20 - productForm.images.length} slots remaining • JPG, PNG, WebP
                            </p>
                          </div>
                        </div>
                      )}
                      
                    </div>
                    
                    {/* Stock Quantity - Only show when NOT using variants */}
                    {!productForm.has_variants && (
                    <div>
                      <label className="text-xs font-mono text-zinc-400 mb-2 block">STOCK QUANTITY *</label>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newStock = Math.max(0, parseInt(productForm.stock || 0) - 1);
                            if (editingProduct && window.confirm(`Decrease stock from ${productForm.stock} to ${newStock}?`)) {
                              setProductForm(p => ({ ...p, stock: newStock.toString() }));
                            } else if (!editingProduct) {
                              setProductForm(p => ({ ...p, stock: newStock.toString() }));
                            }
                          }}
                          className="h-10 w-10 p-0 rounded-none border-zinc-700 text-white hover:bg-zinc-800"
                        >
                          -
                        </Button>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => {
                            if (editingProduct) {
                              const newVal = e.target.value;
                              if (window.confirm(`Change stock from ${productForm.stock} to ${newVal}?`)) {
                                setProductForm(p => ({ ...p, stock: newVal }));
                              }
                            } else {
                              setProductForm(p => ({ ...p, stock: e.target.value }));
                            }
                          }}
                          className="bg-zinc-900 border-zinc-800 text-white rounded-none text-center w-24"
                          required
                          data-testid="product-stock-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newStock = parseInt(productForm.stock || 0) + 1;
                            if (editingProduct && window.confirm(`Increase stock from ${productForm.stock} to ${newStock}?`)) {
                              setProductForm(p => ({ ...p, stock: newStock.toString() }));
                            } else if (!editingProduct) {
                              setProductForm(p => ({ ...p, stock: newStock.toString() }));
                            }
                          }}
                          className="h-10 w-10 p-0 rounded-none border-zinc-700 text-white hover:bg-zinc-800"
                        >
                          +
                        </Button>
                      </div>
                      {editingProduct && (
                        <p className="text-xs text-yellow-500 mt-1">Stock changes require confirmation</p>
                      )}
                    </div>
                    )}
                    
                    <div className="flex items-center justify-between border border-zinc-800 p-4">
                      <span className="font-mono text-sm text-white">ENABLE AUCTION</span>
                      <Switch
                        checked={productForm.is_bidding}
                        onCheckedChange={(v) => {
                          // When enabling auction, set default end time to +24 hours
                          let defaultEndTime = '';
                          if (v && !productForm.bid_end_time) {
                            const d = new Date();
                            d.setHours(d.getHours() + 24);
                            defaultEndTime = formatForDateTimeInput(d);
                          }
                          setProductForm(p => ({ 
                            ...p, 
                            is_bidding: v,
                            // Set default end time when enabling auction
                            bid_end_time: v ? (p.bid_end_time || defaultEndTime) : p.bid_end_time,
                            // Disable variants when auction is enabled
                            has_variants: v ? false : p.has_variants,
                            variants: v ? [] : p.variants
                          }));
                        }}
                        disabled={productForm.has_variants}
                        data-testid="product-bidding-switch"
                      />
                    </div>
                    {productForm.has_variants && (
                      <p className="text-xs text-yellow-500 -mt-2 px-4">Auctions cannot have variants. Disable variants to enable auction.</p>
                    )}
                    
                    {/* Product Variants Section */}
                    <div className="flex items-center justify-between border border-zinc-800 p-4">
                      <div>
                        <span className="font-mono text-sm text-white">ENABLE VARIANTS</span>
                        <p className="text-xs text-zinc-600 mt-1 uppercase">MULTIPLE SIZES/NUMBERS PER PRODUCT</p>
                      </div>
                      <Switch
                        checked={productForm.has_variants}
                        onCheckedChange={(v) => setProductForm(p => ({ 
                          ...p, 
                          has_variants: v,
                          // Disable auction when variants are enabled
                          is_bidding: v ? false : p.is_bidding,
                          reserve_price: v ? '' : p.reserve_price,
                          buy_now_price: v ? '' : p.buy_now_price,
                          bid_end_time: v ? '' : p.bid_end_time
                        }))}
                        disabled={productForm.is_bidding}
                        data-testid="product-variants-switch"
                      />
                    </div>
                    {productForm.is_bidding && (
                      <p className="text-xs text-yellow-500 -mt-2 px-4 uppercase">VARIANTS CANNOT BE USED WITH AUCTIONS. DISABLE AUCTION TO ENABLE VARIANTS.</p>
                    )}
                    
                    {/* Allow Offers Toggle - Only for fixed price items without variants */}
                    {!productForm.is_bidding && !productForm.has_variants && (
                      <div className="flex items-center justify-between border border-zinc-800 p-4">
                        <div>
                          <span className="font-mono text-sm text-white">ALLOW OFFERS</span>
                          <p className="text-xs text-zinc-600 mt-1 uppercase">LET BUYERS NEGOTIATE (MAX 25% DISCOUNT)</p>
                        </div>
                        <Switch
                          checked={productForm.allow_offers}
                          onCheckedChange={(v) => setProductForm(p => ({ ...p, allow_offers: v }))}
                          data-testid="product-offers-switch"
                        />
                      </div>
                    )}
                    
                    {/* Payment Plan Options - Only for non-auction items */}
                    {!productForm.is_bidding && (
                      <div className="border border-zinc-800 p-4">
                        <span className="font-mono text-sm text-white block mb-2">PAYMENT PLANS</span>
                        <p className="text-[10px] text-yellow-500 mb-3 uppercase">+20% ARRANGEMENT FEE ON ALL PLANS</p>
                        
                        {/* Standard Plan Option (2 payments - available for all) */}
                        <div className="border border-zinc-800 p-3 mb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-mono text-xs text-tiffany-500 font-bold">STANDARD PLAN</span>
                              <p className="text-[10px] text-zinc-500 mt-1 uppercase">50% DOWN • 2 PAYMENTS • 7 DAYS • +20% FEE</p>
                            </div>
                            <Switch
                              checked={productForm.standard_plan_enabled}
                              onCheckedChange={(v) => setProductForm(p => ({ ...p, standard_plan_enabled: v }))}
                              data-testid="product-standard-plan-switch"
                            />
                          </div>
                        </div>
                        
                        {/* Flex Pay Option (3 payments - $350+ only) */}
                        <div className="border border-zinc-800 p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-mono text-xs text-orange-400 font-bold">FLEX PAY</span>
                              <p className="text-[10px] text-zinc-500 mt-1 uppercase">33% DOWN • 3 PAYMENTS • 14 DAYS • +20% FEE • $350+ ONLY</p>
                            </div>
                            <Switch
                              checked={productForm.flex_pay_enabled}
                              onCheckedChange={(v) => setProductForm(p => ({ ...p, flex_pay_enabled: v }))}
                              data-testid="product-flex-pay-switch"
                            />
                          </div>
                        </div>
                        
                        <p className="text-[10px] text-red-400 mt-3 uppercase">⚠️ ALL PAYMENTS ARE NON-REFUNDABLE</p>
                      </div>
                    )}
                    
                    {/* Special Product Types */}
                    <div className="border border-purple-500/30 p-4 bg-purple-500/5">
                      <span className="font-mono text-sm text-purple-400 block mb-3">SPECIAL PRODUCT TYPES</span>
                      <p className="text-[10px] text-zinc-500 mb-3">ONLY ONE CAN BE ACTIVE AT A TIME</p>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-mono text-xs text-white">GIFT CARD</span>
                            <p className="text-[10px] text-zinc-500 uppercase">DIGITAL GIFT CARD - BUYER SETS AMOUNT</p>
                          </div>
                          <Switch
                            checked={productForm.is_gift_card}
                            onCheckedChange={(v) => setProductForm(p => ({ 
                              ...p, 
                              is_gift_card: v,
                              is_blkcrd: false
                            }))}
                            data-testid="product-gift-card-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-mono text-xs text-white">BLKCRD MEMBERSHIP</span>
                            <p className="text-[10px] text-zinc-500 uppercase">PREMIUM MEMBERSHIP - 15% OFF + COMING SOON ACCESS</p>
                          </div>
                          <Switch
                            checked={productForm.is_blkcrd}
                            onCheckedChange={(v) => setProductForm(p => ({ 
                              ...p, 
                              is_blkcrd: v,
                              is_gift_card: false
                            }))}
                            data-testid="product-blkcrd-switch"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {productForm.has_variants && (
                      <div className="space-y-4 border border-zinc-800 p-4 bg-zinc-900/50">
                        <p className="text-xs font-mono text-tiffany-500 mb-2">VARIANT SETTINGS</p>
                        
                        {/* Variant Type Selector */}
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-2 block">VARIANT TYPE</label>
                          <div className="flex gap-2">
                            {['size', 'number', 'custom'].map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setProductForm(p => ({ ...p, variant_type: type }))}
                                className={`px-4 py-2 text-xs font-mono border-2 transition-all ${
                                  productForm.variant_type === type
                                    ? 'bg-tiffany-500/20 border-tiffany-500 text-tiffany-500'
                                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                                }`}
                                data-testid={`variant-type-${type}`}
                              >
                                {type.toUpperCase()}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-zinc-600 mt-1">
                            {productForm.variant_type === 'size' && 'e.g., 32mm, 34mm, 36mm'}
                            {productForm.variant_type === 'number' && 'e.g., #001, #002, #003 or 2101, 2102'}
                            {productForm.variant_type === 'custom' && 'e.g., Red, Blue, Limited Edition'}
                          </p>
                        </div>
                        
                        {/* Add New Variant */}
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-2 block">ADD VARIANT</label>
                          <div className="flex flex-col gap-2">
                            <Input
                              placeholder={
                                productForm.variant_type === 'size' ? '32mm' :
                                productForm.variant_type === 'number' ? '#001' : 'Variant name'
                              }
                              value={newVariantName}
                              onChange={(e) => setNewVariantName(e.target.value)}
                              className="bg-zinc-900 border-zinc-800 text-white rounded-none w-full"
                              data-testid="new-variant-name"
                            />
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                placeholder="Stock"
                                value={newVariantStock}
                                onChange={(e) => setNewVariantStock(e.target.value)}
                                className="bg-zinc-900 border-zinc-800 text-white rounded-none w-16 text-xs"
                                data-testid="new-variant-stock"
                              />
                              <Input
                                type="number"
                                placeholder="Price $"
                                value={newVariantPrice}
                                onChange={(e) => setNewVariantPrice(e.target.value)}
                                className="bg-zinc-900 border-zinc-800 text-white rounded-none w-16 text-xs"
                                data-testid="new-variant-price"
                              />
                              <Input
                                type="number"
                                placeholder="Cost $"
                                value={newVariantCost}
                                onChange={(e) => setNewVariantCost(e.target.value)}
                                className="bg-zinc-900 border-zinc-800 text-white rounded-none w-16 text-xs"
                                data-testid="new-variant-cost"
                              />
                              <Button
                                type="button"
                                onClick={addVariant}
                                className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none px-4"
                                data-testid="add-variant-btn"
                              >
                                ADD
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Variant List */}
                        {productForm.variants.length > 0 && (
                          <div>
                            <label className="text-xs font-mono text-zinc-400 mb-2 block">
                              VARIANTS ({productForm.variants.length})
                            </label>
                            <div className="space-y-3">
                              {productForm.variants.map((variant, idx) => {
                                const selectedIndices = getVariantImageIndices(variant);
                                return (
                                  <div 
                                    key={variant.id} 
                                    className="bg-zinc-900 border border-zinc-800 p-3"
                                    data-testid={`variant-item-${idx}`}
                                  >
                                    {/* Variant Header Row - stack on mobile for more name space */}
                                    <div className="flex flex-col gap-2 mb-2">
                                      {/* Variant name row - full width */}
                                      <div className="flex items-center gap-2">
                                        {/* Drag handle and reorder buttons */}
                                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                                          <button
                                            type="button"
                                            onClick={() => moveVariant(idx, -1)}
                                            disabled={idx === 0}
                                            className="text-zinc-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                                            title="Move up"
                                          >
                                            <ChevronUp className="w-4 h-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => moveVariant(idx, 1)}
                                            disabled={idx === productForm.variants.length - 1}
                                            className="text-zinc-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                                            title="Move down"
                                          >
                                            <ChevronDown className="w-4 h-4" />
                                          </button>
                                        </div>
                                        
                                        {/* Editable variant name - full width */}
                                        <Input
                                          type="text"
                                          value={variant.name}
                                          onChange={(e) => updateVariantName(variant.id, e.target.value)}
                                          className="bg-zinc-800 border-zinc-700 text-white rounded-none flex-1 h-8 font-mono font-bold text-sm"
                                          data-testid={`variant-name-${idx}`}
                                        />
                                        
                                        {/* Delete button */}
                                        <button
                                          type="button"
                                          onClick={() => removeVariant(variant.id)}
                                          className="text-red-500 hover:text-red-400 p-1 flex-shrink-0"
                                        >
                                          ×
                                        </button>
                                      </div>
                                      
                                      {/* Stock controls row */}
                                      <div className="flex flex-wrap items-center gap-1 pl-7">
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-zinc-500 mr-1">Stock:</span>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => decrementVariantStock(variant.id)}
                                            disabled={variant.stock <= 0}
                                            className="h-8 w-8 p-0 rounded-none border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
                                            data-testid={`variant-stock-dec-${idx}`}
                                          >
                                            -
                                          </Button>
                                          <Input
                                            type="text"
                                            inputMode="numeric"
                                            value={variant.stock.toString()}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/^0+(?=\d)/, '');
                                              const parsed = val === '' ? 0 : parseInt(val);
                                              if (!isNaN(parsed)) {
                                                updateVariantStock(variant.id, parsed);
                                              }
                                            }}
                                            onFocus={(e) => e.target.select()}
                                            className="bg-zinc-800 border-zinc-700 text-white rounded-none w-12 h-8 text-center text-xs"
                                            data-testid={`variant-stock-${idx}`}
                                          />
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => incrementVariantStock(variant.id)}
                                            className="h-8 w-8 p-0 rounded-none border-zinc-700 text-white hover:bg-zinc-800"
                                            data-testid={`variant-stock-inc-${idx}`}
                                          >
                                            +
                                          </Button>
                                        </div>
                                        
                                        {/* Cost input for variant */}
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-zinc-500">Cost:</span>
                                          <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0"
                                              value={variant.cost !== null && variant.cost !== undefined ? variant.cost : ''}
                                              onChange={(e) => updateVariantCost(variant.id, e.target.value)}
                                              onFocus={(e) => e.target.select()}
                                              className={`bg-zinc-800 border-zinc-700 text-white rounded-none w-20 h-8 pl-5 text-xs ${
                                                (variant.cost === null || variant.cost === undefined) ? 'border-red-500/50' : ''
                                              }`}
                                              data-testid={`variant-cost-${idx}`}
                                            />
                                          </div>
                                        </div>
                                        {/* Price input for variant */}
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-zinc-500">Price:</span>
                                          <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder={productForm.price || '0'}
                                              value={variant.price !== null && variant.price !== undefined ? variant.price : ''}
                                              onChange={(e) => updateVariantPrice(variant.id, e.target.value)}
                                              onFocus={(e) => e.target.select()}
                                              className="bg-zinc-800 border-zinc-700 text-white rounded-none w-20 h-8 pl-5 text-xs"
                                              data-testid={`variant-price-${idx}`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Variant Image Selector - Multi-select */}
                                    {productForm.images && productForm.images.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-zinc-800">
                                        <p className="text-xs text-zinc-500 mb-2">
                                          Select images for this variant ({selectedIndices.length} selected):
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                          {productForm.images.map((img, imgIdx) => {
                                            const isSelected = selectedIndices.includes(imgIdx);
                                            return (
                                              <button
                                                key={imgIdx}
                                                type="button"
                                                onClick={() => toggleVariantImageIndex(variant.id, imgIdx)}
                                                className={`relative w-12 h-12 border-2 transition-all ${
                                                  isSelected 
                                                    ? 'border-tiffany-500 ring-1 ring-tiffany-500/50' 
                                                    : 'border-zinc-700 hover:border-zinc-500'
                                                }`}
                                                data-testid={`variant-${idx}-image-${imgIdx}`}
                                              >
                                                <img 
                                                  src={thumbUrl(img, 'thumb')} 
                                                  alt={`Image ${imgIdx + 1}`}
                                                  className="w-full h-full object-cover"
                                                  loading="lazy"
                                                  decoding="async"
                                                />
                                                {isSelected && (
                                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-tiffany-500 text-black text-[10px] font-bold flex items-center justify-center">
                                                    {selectedIndices.indexOf(imgIdx) + 1}
                                                  </div>
                                                )}
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] text-center py-0.5">
                                                  #{imgIdx + 1}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                              Total stock: {productForm.variants.reduce((sum, v) => sum + (v.stock || 0), 0)}
                              {productForm.images && productForm.images.length > 0 && (
                                <span className="ml-2">• Click images to assign them to each variant (multi-select)</span>
                              )}
                            </p>
                            {editingProduct && (
                              <p className="text-xs text-yellow-500 mt-1">Stock changes require confirmation</p>
                            )}
                          </div>
                        )}
                        
                        {productForm.variants.length === 0 && (
                          <p className="text-xs text-yellow-500">Add at least one variant before saving</p>
                        )}
                      </div>
                    )}

                    {productForm.is_bidding && (
                      <div className="space-y-4 border border-zinc-800 p-4 bg-zinc-900/50">
                        <p className="text-xs font-mono text-tiffany-500 mb-2">AUCTION SETTINGS</p>
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">STARTING BID ($) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={productForm.price}
                            onChange={(e) => setProductForm(p => ({ ...p, price: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                            required
                            data-testid="product-starting-bid-input"
                          />
                          <p className="text-xs text-zinc-600 mt-1 uppercase">FIRST BID MUST MEET OR EXCEED THIS AMOUNT</p>
                        </div>
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">RESERVE PRICE ($) <span className="text-zinc-600">(OPTIONAL)</span></label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="MINIMUM PRICE TO SELL"
                            value={productForm.reserve_price}
                            onChange={(e) => setProductForm(p => ({ ...p, reserve_price: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800 text-white rounded-none uppercase placeholder:uppercase"
                            data-testid="product-reserve-input"
                          />
                          <p className="text-xs text-zinc-600 mt-1 uppercase">ITEM WON'T SELL UNLESS BIDDING REACHES THIS PRICE</p>
                        </div>
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">BUY IT NOW PRICE ($) <span className="text-zinc-600">(OPTIONAL)</span></label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="INSTANT PURCHASE PRICE"
                            value={productForm.buy_now_price}
                            onChange={(e) => setProductForm(p => ({ ...p, buy_now_price: e.target.value }))}
                            className="bg-zinc-900 border-zinc-800 text-white rounded-none uppercase placeholder:uppercase"
                            data-testid="product-buynow-input"
                          />
                          <p className="text-xs text-zinc-600 mt-1 uppercase">BUYERS CAN SKIP AUCTION AND BUY INSTANTLY AT THIS PRICE</p>
                        </div>
                        <div>
                          <label className="text-xs font-mono text-zinc-400 mb-1 block">AUCTION END TIME (EASTERN) *</label>
                          <input
                            type="datetime-local"
                            value={productForm.bid_end_time || ''}
                            onChange={(e) => setProductForm(p => ({ ...p, bid_end_time: e.target.value }))}
                            className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 text-white rounded-none focus:outline-none focus:border-tiffany-500"
                            style={{ 
                              colorScheme: 'dark',
                              WebkitAppearance: 'none'
                            }}
                            required
                            data-testid="product-endtime-input"
                          />
                          <div className="flex gap-2 mt-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setHours(d.getHours() + 8);
                                setProductForm(p => ({ ...p, bid_end_time: formatForDateTimeInput(d) }));
                              }}
                              className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:text-tiffany-500 border border-zinc-700 hover:border-tiffany-500/50"
                            >
                              +8 HRS
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setHours(d.getHours() + 12);
                                setProductForm(p => ({ ...p, bid_end_time: formatForDateTimeInput(d) }));
                              }}
                              className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:text-tiffany-500 border border-zinc-700 hover:border-tiffany-500/50"
                            >
                              +12 HRS
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setHours(d.getHours() + 24);
                                setProductForm(p => ({ ...p, bid_end_time: formatForDateTimeInput(d) }));
                              }}
                              className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:text-tiffany-500 border border-zinc-700 hover:border-tiffany-500/50"
                            >
                              +24 HRS
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setDate(d.getDate() + 2);
                                setProductForm(p => ({ ...p, bid_end_time: formatForDateTimeInput(d) }));
                              }}
                              className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:text-tiffany-500 border border-zinc-700 hover:border-tiffany-500/50"
                            >
                              +2 DAYS
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setDate(d.getDate() + 7);
                                setProductForm(p => ({ ...p, bid_end_time: formatForDateTimeInput(d) }));
                              }}
                              className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:text-tiffany-500 border border-zinc-700 hover:border-tiffany-500/50"
                            >
                              +1 WEEK
                            </button>
                          </div>
                          {productForm.bid_end_time && (
                            <p className="text-xs text-tiffany-500 mt-2 uppercase">
                              ENDS: {(() => {
                                try {
                                  // Handle both ISO strings and datetime-local format
                                  let date;
                                  if (productForm.bid_end_time.includes('Z') || productForm.bid_end_time.includes('+')) {
                                    // ISO format from database - parse directly
                                    date = new Date(productForm.bid_end_time);
                                  } else {
                                    // datetime-local format - treat as local time
                                    date = new Date(productForm.bid_end_time);
                                  }
                                  return date.toLocaleString('en-US', {
                                    timeZone: 'America/New_York',
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  }) + ' ET';
                                } catch {
                                  return 'INVALID DATE';
                                }
                              })()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setProductDialogOpen(false)}
                        className="flex-1 border-zinc-600 text-zinc-400 hover:bg-zinc-800 rounded-none"
                      >
                        CANCEL
                      </Button>
                      <Button
                        type="submit"
                        disabled={productSubmitting}
                        className="flex-1 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="product-submit-btn"
                      >
                        {productSubmitting ? 'SAVING...' : (editingProduct ? 'UPDATE PRODUCT' : 'CREATE PRODUCT')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            
            {/* Compact Search & Filter */}
            <div className="flex flex-col gap-2">
              {/* Row 1: Search bar and Brand filter */}
              <div className="flex gap-2 items-center">
                <div className="flex-1 relative">
                  <Input
                    type="text"
                    placeholder="SEARCH..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white rounded-none pl-8 h-8 font-mono text-xs w-full uppercase"
                    data-testid="product-search-input"
                  />
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <Select value={productBrandFilter} onValueChange={setProductBrandFilter}>
                  <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-xs">
                    <SelectValue placeholder="BRAND" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                    <SelectItem value="all" className="font-mono text-xs">ALL</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.name} className="font-mono text-xs uppercase">
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Row 2: Cost filter */}
              <div className="flex gap-2 items-center">
                <Select value={productCostFilter} onValueChange={setProductCostFilter}>
                  <SelectTrigger className={`w-36 bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-xs ${productCostFilter === 'missing' ? 'border-red-500 text-red-400' : ''}`}>
                    <SelectValue placeholder="COST" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                    <SelectItem value="all" className="font-mono text-xs">ALL COSTS</SelectItem>
                    <SelectItem value="missing" className="font-mono text-xs text-red-400">MISSING COST</SelectItem>
                    <SelectItem value="has" className="font-mono text-xs text-green-400">HAS COST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Stock Change Confirmation Dialog */}
            <Dialog open={!!pendingStockChange} onOpenChange={(open) => !open && cancelStockChange()}>
                <DialogContent className="bg-[#0a0a0a] border-zinc-800 rounded-none max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="font-sans text-xl text-white">
                      CONFIRM STOCK CHANGE
                    </DialogTitle>
                  </DialogHeader>
                  <div className="mt-4 space-y-4">
                    <p className="text-zinc-400 font-mono text-sm">
                      Change stock for <span className="text-tiffany-500 font-bold">{pendingStockChange?.variantName}</span>?
                    </p>
                    <div className="flex items-center justify-center gap-4 text-center">
                      <div className="bg-zinc-900 border border-zinc-800 p-3 min-w-[80px]">
                        <p className="text-xs text-zinc-500 font-mono mb-1">FROM</p>
                        <p className="text-2xl text-red-400 font-mono">{pendingStockChange?.oldStock}</p>
                      </div>
                      <span className="text-zinc-500">→</span>
                      <div className="bg-zinc-900 border border-zinc-800 p-3 min-w-[80px]">
                        <p className="text-xs text-zinc-500 font-mono mb-1">TO</p>
                        <p className="text-2xl text-green-400 font-mono">{pendingStockChange?.newStock}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        onClick={cancelStockChange}
                        variant="outline"
                        className="flex-1 rounded-none border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        data-testid="stock-change-cancel"
                      >
                        CANCEL
                      </Button>
                      <Button
                        type="button"
                        onClick={confirmStockChange}
                        className="flex-1 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                        data-testid="stock-change-confirm"
                      >
                        CONFIRM
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

            {/* Image Tools Section */}
            <div className="border border-zinc-800 mb-4">
              <button
                onClick={() => setShowImageTools(!showImageTools)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors"
              >
                <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1.5">
                  <Image className="w-3 h-3" />
                  IMAGE TOOLS
                </span>
                {showImageTools ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
              </button>
              
              {showImageTools && (
                <div className="p-4 space-y-3">
                  <p className="font-mono text-[10px] text-zinc-500">FIX SIDEWAYS IMAGES AND OPTIMIZE THUMBNAILS</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          toast.info('FIXING IMAGE ORIENTATIONS...');
                          const res = await axios.post(`${API}/admin/images/fix-orientation`, {}, { headers: getAuthHeader() });
                          toast.success(`FIXED ${res.data.fixed} IMAGES (${res.data.skipped} SKIPPED)`);
                        } catch (e) {
                          toast.error('FAILED TO FIX ORIENTATIONS');
                        }
                      }}
                      className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none text-xs"
                    >
                      FIX SIDEWAYS IMAGES
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          toast.info('WARMING IMAGE CACHE...');
                          const res = await axios.post(`${API}/admin/images/warm-cache`, {}, { headers: getAuthHeader() });
                          toast.success(`CACHED ${res.data.warmed} THUMBNAILS`);
                        } catch (e) {
                          toast.error('FAILED TO WARM CACHE');
                        }
                      }}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none text-xs"
                    >
                      PRE-LOAD THUMBNAILS
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await axios.delete(`${API}/admin/images/clear-cache`, { headers: getAuthHeader() });
                          toast.success(`CLEARED ${res.data.deleted} CACHED THUMBNAILS`);
                        } catch (e) {
                          toast.error('FAILED TO CLEAR CACHE');
                        }
                      }}
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-none text-xs"
                    >
                      CLEAR CACHE
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Brand Manager Section - Compact */}
            <div className="border border-zinc-800">
              <button
                onClick={() => setShowBrandManager(!showBrandManager)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors"
              >
                <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1.5">
                  <Palette className="w-3 h-3" />
                  BRANDS ({brands.length})
                </span>
                {showBrandManager ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
              </button>
              
              {showBrandManager && (
                <div className="p-4 space-y-4">
                  {/* Add New Brand */}
                  <div className="flex flex-wrap gap-2 items-end pb-4 border-b border-zinc-800">
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-xs font-mono text-zinc-500 mb-1 block">NEW BRAND NAME</label>
                      <Input
                        value={newBrandName}
                        onChange={(e) => {
                          const input = e.target;
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          setNewBrandName(e.target.value.toUpperCase());
                          requestAnimationFrame(() => input.setSelectionRange(start, end));
                        }}
                        placeholder="ENTER BRAND NAME"
                        className="bg-zinc-900 border-zinc-700 text-white rounded-none h-9 uppercase"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-mono text-zinc-500 mb-1 block">COLOR</label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={newBrandColor}
                          onChange={(e) => setNewBrandColor(e.target.value)}
                          className="w-9 h-9 cursor-pointer bg-transparent border border-zinc-700"
                        />
                        <Input
                          value={newBrandColor}
                          onChange={(e) => setNewBrandColor(e.target.value)}
                          className="bg-zinc-900 border-zinc-700 text-white rounded-none h-9 w-24 font-mono text-xs"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleCreateBrand}
                      disabled={!newBrandName.trim()}
                      className="bg-tiffany-500 hover:bg-tiffany-400 text-black rounded-none h-9"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      ADD
                    </Button>
                  </div>

                  {/* Brand List */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {brands.map(brand => (
                      <div 
                        key={brand.id}
                        className="flex items-center gap-2 p-2 border border-zinc-800 bg-zinc-900/30"
                      >
                        {editingBrand?.id === brand.id ? (
                          <>
                            <input
                              type="color"
                              value={editingBrand.color}
                              onChange={(e) => setEditingBrand({ ...editingBrand, color: e.target.value })}
                              className="w-8 h-8 cursor-pointer bg-transparent border-0"
                            />
                            <Input
                              value={editingBrand.name}
                              onChange={(e) => {
                                const input = e.target;
                                const start = input.selectionStart;
                                const end = input.selectionEnd;
                                setEditingBrand({ ...editingBrand, name: e.target.value.toUpperCase() });
                                requestAnimationFrame(() => input.setSelectionRange(start, end));
                              }}
                              className="flex-1 bg-zinc-800 border-zinc-700 text-white rounded-none h-8 text-xs uppercase"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdateBrand(brand.id, editingBrand.name, editingBrand.color)}
                              className="h-8 px-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-none"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setEditingBrand(null)}
                              className="h-8 px-2 bg-zinc-700 text-zinc-400 hover:bg-zinc-600 rounded-none"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span 
                              className="w-6 h-6 flex-shrink-0 border border-zinc-700" 
                              style={{ backgroundColor: brand.color }}
                            />
                            <span className="flex-1 font-mono text-xs text-white truncate uppercase">
                              {brand.name.toUpperCase()}
                            </span>
                            <span className="font-mono text-[10px] text-zinc-500">
                              {brand.usage_count || 0}
                            </span>
                            <button
                              onClick={() => setEditingBrand({ id: brand.id, name: brand.name, color: brand.color })}
                              className="p-1 text-zinc-500 hover:text-tiffany-500 transition-colors"
                              title="Edit brand"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteBrand(brand.id, brand.name)}
                              className="p-1 text-zinc-500 hover:text-red-500 transition-colors"
                              title="Delete brand"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {brands.length === 0 && (
                    <p className="text-center text-zinc-500 font-mono text-sm py-4">
                      No brands created yet. Add one above!
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Categories Manager Section - Collapsible */}
            <div className="border border-zinc-800 mb-4">
              <button
                onClick={() => setShowCategoryManager(!showCategoryManager)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors"
              >
                <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1.5">
                  <Tag className="w-3 h-3" />
                  CATEGORIES ({adminCategories.length})
                </span>
                {showCategoryManager ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
              </button>
              
              {showCategoryManager && (
                <div className="p-4 space-y-4">
                  {/* Add New Category */}
                  <div className="flex gap-2 pb-4 border-b border-zinc-800">
                    <Input
                      placeholder="NEW CATEGORY NAME..."
                      value={newCategoryName}
                      onChange={(e) => {
                        const input = e.target;
                        const start = input.selectionStart;
                        const end = input.selectionEnd;
                        setNewCategoryName(e.target.value.toUpperCase());
                        requestAnimationFrame(() => input.setSelectionRange(start, end));
                      }}
                      className="bg-zinc-900 border-zinc-700 text-white rounded-none font-mono text-xs flex-1 h-9 uppercase"
                    />
                    <Button
                      onClick={async () => {
                        if (!newCategoryName.trim()) return;
                        try {
                          await axios.post(`${API}/admin/categories`, { name: newCategoryName.trim().toUpperCase() }, { headers: getAuthHeader() });
                          toast.success(`CATEGORY "${newCategoryName.toUpperCase()}" ADDED`);
                          setNewCategoryName('');
                          fetchData();
                        } catch (error) {
                          toast.error(errMsg(error, 'FAILED TO ADD CATEGORY'));
                        }
                      }}
                      disabled={!newCategoryName.trim()}
                      className="bg-tiffany-500 hover:bg-tiffany-400 text-black rounded-none h-9"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      ADD
                    </Button>
                  </div>

                  {/* Category List */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {adminCategories.map((cat, index) => (
                      <div 
                        key={cat.id}
                        className="flex items-center gap-2 p-2 border border-zinc-800 bg-zinc-900/30"
                      >
                        {editingCategory?.id === cat.id ? (
                          <>
                            <Input
                              value={editingCategory.name}
                              onChange={(e) => {
                                const input = e.target;
                                const start = input.selectionStart;
                                const end = input.selectionEnd;
                                setEditingCategory({ ...editingCategory, name: e.target.value.toUpperCase() });
                                requestAnimationFrame(() => input.setSelectionRange(start, end));
                              }}
                              className="flex-1 bg-zinc-800 border-zinc-700 text-white rounded-none h-8 text-xs uppercase"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/categories/${cat.id}`, { name: editingCategory.name.toUpperCase() }, { headers: getAuthHeader() });
                                  toast.success('CATEGORY UPDATED');
                                  setEditingCategory(null);
                                  fetchData();
                                } catch (error) {
                                  toast.error('FAILED TO UPDATE');
                                }
                              }}
                              className="h-8 px-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-none"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setEditingCategory(null)}
                              className="h-8 px-2 bg-zinc-700 text-zinc-400 hover:bg-zinc-600 rounded-none"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Reorder buttons */}
                            <div className="flex flex-col gap-0.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={index === 0}
                                onClick={async () => {
                                  const newOrder = [...adminCategories];
                                  [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                  const categoryIds = newOrder.map(c => c.id);
                                  try {
                                    await axios.put(`${API}/admin/categories/reorder`, { category_ids: categoryIds }, { headers: getAuthHeader() });
                                    fetchData();
                                  } catch (error) {
                                    toast.error('FAILED TO REORDER');
                                  }
                                }}
                                className="h-4 w-4 p-0 text-zinc-500 hover:text-white disabled:opacity-30"
                              >
                                <ChevronUp className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={index === adminCategories.length - 1}
                                onClick={async () => {
                                  const newOrder = [...adminCategories];
                                  [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                  const categoryIds = newOrder.map(c => c.id);
                                  try {
                                    await axios.put(`${API}/admin/categories/reorder`, { category_ids: categoryIds }, { headers: getAuthHeader() });
                                    fetchData();
                                  } catch (error) {
                                    toast.error('FAILED TO REORDER');
                                  }
                                }}
                                className="h-4 w-4 p-0 text-zinc-500 hover:text-white disabled:opacity-30"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </div>
                            <span className="flex-1 font-mono text-xs text-white truncate uppercase">
                              {cat.name}
                            </span>
                            <button
                              onClick={() => setEditingCategory({ id: cat.id, name: cat.name })}
                              className="p-1 text-zinc-500 hover:text-tiffany-500 transition-colors"
                              title="Edit category"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm(`DELETE CATEGORY "${cat.name}"?`)) {
                                  try {
                                    await axios.delete(`${API}/admin/categories/${cat.id}`, { headers: getAuthHeader() });
                                    toast.success('CATEGORY DELETED');
                                    fetchData();
                                  } catch (error) {
                                    toast.error('FAILED TO DELETE');
                                  }
                                }
                              }}
                              className="p-1 text-zinc-500 hover:text-red-500 transition-colors"
                              title="Delete category"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {adminCategories.length === 0 && (
                    <p className="text-center text-zinc-500 font-mono text-sm py-4 uppercase">
                      NO CATEGORIES CREATED YET
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Product Order Manager Section */}
            <div className="border border-zinc-800 mb-4">
              <button
                onClick={() => setShowProductOrder(!showProductOrder)}
                className="w-full flex items-center justify-between px-3 py-1.5 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors"
              >
                <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1.5">
                  <GripVertical className="w-3 h-3" />
                  CATALOG ORDER
                </span>
                {showProductOrder ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
              </button>
              
              {showProductOrder && (
                <div className="p-3">
                  <ProductOrderManager 
                    brands={brands}
                    products={products}
                    getAuthHeader={getAuthHeader}
                    onUpdate={fetchData}
                  />
                </div>
              )}
            </div>


            {/* Sold Cover Photo Alert */}
            {soldCoverPhotos.length > 0 && (
              <div className="border border-orange-500/30 bg-orange-500/5 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                  <span className="font-mono text-xs text-orange-400">
                    {soldCoverPhotos.length} PRODUCT{soldCoverPhotos.length > 1 ? 'S' : ''} WITH SOLD-OUT COVER PHOTO
                  </span>
                </div>
                <div className="space-y-2">
                  {soldCoverPhotos.map((item) => (
                    <div key={item.product_id} className="flex items-center justify-between gap-2 bg-zinc-900/50 p-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {item.current_thumbnail && (
                          <img 
                            src={item.current_thumbnail.startsWith('/') ? `${'https://shopblkmrkt.com'}${item.current_thumbnail}` : item.current_thumbnail} 
                            alt="" 
                            className="w-8 h-8 object-cover flex-shrink-0 opacity-50"
                          />
                        )}
                        <div className="min-w-0">
                          <span className="font-mono text-[10px] text-white block truncate">{item.product_name}</span>
                          <span className="font-mono text-[9px] text-orange-400">
                            COVER = SOLD "{item.sold_variant}" • {item.in_stock_count}/{item.total_variants} IN STOCK
                          </span>
                        </div>
                      </div>
                      {item.suggested_replacement && (
                        <button
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/admin/products/${item.product_id}/reassign-cover`, 
                                { image_index: item.suggested_replacement.image_index }, 
                                { headers: getAuthHeader() }
                              );
                              toast.success(`COVER UPDATED TO "${item.suggested_replacement.variant_name}" VARIANT`);
                              fetchData();
                            } catch (e) {
                              toast.error('FAILED TO REASSIGN COVER');
                            }
                          }}
                          className="font-mono text-[9px] bg-orange-500/20 text-orange-400 px-2 py-1 hover:bg-orange-500/30 flex-shrink-0"
                        >
                          USE "{item.suggested_replacement.variant_name}"
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-zinc-800 overflow-x-auto" data-testid="products-table">
              {/* LOT Controls - Compact */}
              {lots.length > 0 && (
                <div className="px-3 py-2 bg-zinc-900/80 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-zinc-500">LOTS:</span>
                  {lots.map(lot => (
                    <div 
                      key={lot.lot_number} 
                      className={`flex items-center gap-2 px-2 py-1 border rounded text-xs ${
                        lot.is_fully_online 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-zinc-700 bg-zinc-800/50'
                      }`}
                    >
                      <span className="font-mono text-white font-bold">{lot.lot_number}</span>
                      <span className="font-mono text-[10px] text-zinc-500">
                        {lot.total_items}
                        {lot.true_total !== lot.total_items && (
                          <span className="text-yellow-500 ml-0.5">({lot.true_total})</span>
                        )}
                      </span>
                      <Switch
                        checked={lot.is_fully_online}
                        onCheckedChange={(checked) => {
                          const action = checked ? 'SHOW' : 'HIDE';
                          if (window.confirm(`${action} all ${lot.total_items} products in ${lot.lot_number}?`)) {
                            if (checked) {
                              handleSetLotOnline(lot.lot_number);
                            } else {
                              handleSetLotOffline(lot.lot_number);
                            }
                          }
                        }}
                        className={`scale-75 ${lot.is_fully_online ? 'data-[state=checked]:bg-green-500' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Flat Product List */}
              {(() => {
                const filteredProducts = products
                  .filter(product => {
                    // Multi-word search - all words must match somewhere in name/brand/category/lot
                    const searchWords = productSearch.toLowerCase().split(/\s+/).filter(w => w.length > 0);
                    const matchesSearch = !productSearch || searchWords.every(word => 
                      product.name?.toLowerCase().includes(word) ||
                      product.brand?.toLowerCase().includes(word) ||
                      product.category?.toLowerCase().includes(word) ||
                      product.lot_number?.toLowerCase().includes(word)
                    );
                    const matchesBrand = productBrandFilter === 'all' || product.brand === productBrandFilter;
                    // Cost filter - skip gift cards and BLKCRD since they don't need cost
                    const isDigitalProduct = product.is_gift_card || product.is_blkcrd;
                    // For variant products, check if ANY variant is missing cost
                    // For non-variant products, check the product cost
                    // Accept cost as number OR numeric string (e.g., "50" or 50)
                    const isValidCost = (cost) => {
                      if (cost === null || cost === undefined || cost === '') return false;
                      if (typeof cost === 'number') return true;
                      if (typeof cost === 'string' && cost.trim() !== '' && !isNaN(parseFloat(cost))) return true;
                      return false;
                    };
                    let hasCost;
                    if (product.has_variants && product.variants?.length > 0) {
                      // All variants must have cost for the product to "have cost"
                      hasCost = product.variants.every(v => isValidCost(v.cost));
                    } else {
                      hasCost = isValidCost(product.cost);
                    }
                    const matchesCost = productCostFilter === 'all' || 
                      (productCostFilter === 'missing' && !hasCost && !isDigitalProduct) ||
                      (productCostFilter === 'has' && (hasCost || isDigitalProduct));
                    return matchesSearch && matchesBrand && matchesCost;
                  })
                  .sort((a, b) => (a.display_order || 9999) - (b.display_order || 9999));
                
                const handleMoveProduct = async (productIndex, direction) => {
                  const newIndex = direction === 'up' ? productIndex - 1 : productIndex + 1;
                  if (newIndex < 0 || newIndex >= filteredProducts.length) return;
                  
                  // Get the two products being swapped
                  const product1 = filteredProducts[productIndex];
                  const product2 = filteredProducts[newIndex];
                  
                  // Get their current display_order values (or use their filtered index as fallback)
                  const order1 = product1.display_order ?? productIndex;
                  const order2 = product2.display_order ?? newIndex;
                  
                  // Swap display_order values locally for instant feedback
                  const newProducts = products.map(p => {
                    if (p.id === product1.id) return { ...p, display_order: order2 };
                    if (p.id === product2.id) return { ...p, display_order: order1 };
                    return p;
                  });
                  setProducts(newProducts);
                  
                  // Save just the two swapped products to backend
                  try {
                    await Promise.all([
                      axios.put(`${API}/admin/products/${product1.id}/display-order`, 
                        { display_order: order2 }, { headers: getAuthHeader() }),
                      axios.put(`${API}/admin/products/${product2.id}/display-order`, 
                        { display_order: order1 }, { headers: getAuthHeader() })
                    ]);
                  } catch (error) {
                    toast.error('Failed to save order');
                    fetchData();
                  }
                };
                
                return filteredProducts.map((product, index) => {
                const visibility = product.visibility || (product.is_online === false ? 'offline' : 'online');
                const isOnline = visibility === 'online';
                const isComingSoon = visibility === 'coming_soon';
                
                return (
                  <div 
                    key={product.id} 
                    className={`px-3 py-2.5 flex items-center gap-2 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/50 ${
                      visibility === 'offline' ? 'opacity-50' : ''
                    } ${product.is_consignment ? 'border-l-2 border-l-purple-500/60' : ''}`}
                    data-testid={`product-row-${product.id}`}
                  >
                    {/* Reorder Arrows */}
                    <div className="flex flex-col gap-0 flex-shrink-0">
                      <button
                        onClick={() => handleMoveProduct(index, 'up')}
                        disabled={index === 0}
                        className={`p-1 ${index === 0 ? 'text-zinc-700' : 'text-zinc-500 hover:text-tiffany-500'}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMoveProduct(index, 'down')}
                        disabled={index === filteredProducts.length - 1}
                        className={`p-1 ${index === filteredProducts.length - 1 ? 'text-zinc-700' : 'text-zinc-500 hover:text-tiffany-500'}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Visibility indicator */}
                    <div 
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        isOnline ? 'bg-green-500' : isComingSoon ? 'bg-yellow-500' : 'bg-zinc-600'
                      }`}
                    />
                    
                    {/* Product Info */}
                    <div className="min-w-0 flex-1">
                      <Link 
                        to={`/product/${product.id}`}
                        className="font-mono text-white text-sm hover:text-tiffany-500 transition-colors uppercase inline-flex items-center gap-1.5"
                      >
                        {product.is_consignment && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[9px] tracking-widest"
                            title="Consignment listing — seller earns commission on sale"
                            data-testid={`consignment-badge-${product.id}`}
                          >
                            <Handshake size={9} /> CNGMT
                          </span>
                        )}
                        {product.name}
                      </Link>
                      <div className="font-mono text-xs text-zinc-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span className="uppercase">{product.category}</span>
                        <span className="text-zinc-600">•</span>
                        <span className="text-white">${product.price.toFixed(0)}</span>
                        {product.is_consignment && typeof product.cost === 'number' && product.cost > 0 && (
                          <>
                            <span className="text-zinc-600">•</span>
                            <span className="text-purple-300" title="Floor / minimum owed to consignor">OWED ${product.cost.toFixed(0)}</span>
                          </>
                        )}
                        <span className="text-zinc-600">•</span>
                        <span className={product.stock === 0 ? 'text-red-500' : ''}>{product.stock} left</span>
                        {restockNotifications[product.id] > 0 && (
                          <>
                            <span className="text-zinc-600">•</span>
                            <span className="text-yellow-500" title="Restock notifications">{restockNotifications[product.id]} waiting</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(product)}
                        className="text-tiffany-500 hover:bg-tiffany-500 hover:text-black border-tiffany-500/50 h-8 w-8 p-0 rounded-none"
                        title="Edit"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {/* Visibility Slider Toggle */}
                      <button
                        onClick={() => handleToggleVisibility(product)}
                        className={`h-8 px-2 font-mono text-[10px] font-bold transition-all ${
                          isOnline ? 'bg-green-500 text-black' : 
                          isComingSoon ? 'bg-yellow-500 text-black' : 
                          'bg-red-500/80 text-white'
                        }`}
                        title={`Click to change (Current: ${visibility.toUpperCase()})`}
                        data-testid={`toggle-visibility-${product.id}`}
                      >
                        {isOnline ? 'ON' : isComingSoon ? 'SOON' : 'OFF'}
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-zinc-500 hover:text-red-500 h-8 w-8 p-0"
                        title="Delete"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDuplicate(product)}
                        className="text-zinc-600 hover:text-amber-500 h-8 w-8 p-0"
                        title="Duplicate"
                        data-testid={`duplicate-product-${product.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const shareUrl = `https://shopblkmrkt.com/api/p/${product.id}`;
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Share link copied! Paste on social media.');
                        }}
                        className="text-zinc-600 hover:text-tiffany-500 h-8 w-8 p-0"
                        title="Copy share link for social media"
                        data-testid={`share-product-${product.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              });
              })()}
              {products.length === 0 && (
                <div className="p-8 text-center">
                  <p className="font-mono text-zinc-500">NO PRODUCTS YET</p>
                </div>
              )}
            </div>
            
            {/* Image URL Fix Tool */}
            <div className="border border-zinc-800 p-6 space-y-4 max-w-lg mt-6">
              <h3 className="font-mono text-white text-sm">IMAGE URL FIXER</h3>
              <p className="font-mono text-xs text-zinc-500">
                Fix broken product images caused by old preview URLs. Run this after deploying to a new domain.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/all-image-urls`, { headers: getAuthHeader() });
                      const { summary, total_products } = res.data;
                      
                      let message = `📊 Image Status (${total_products} products)\n\n`;
                      message += `✓ Working: ${summary.working}\n`;
                      message += `✗ Missing files: ${summary.missing}\n`;
                      message += `○ No image set: ${summary.empty}\n`;
                      message += `⚠ Broken URLs: ${summary.broken_urls}`;
                      
                      if (summary.missing > 0) {
                        const missing = [...(res.data.details.missing_files || []), ...(res.data.details.missing_db_images || [])];
                        toast.error(message, { duration: 10000 });
                        console.log('Products needing image re-upload:', missing);
                        alert(`⚠️ ${summary.missing} products have MISSING image files!\n\nThese images were lost and need to be RE-UPLOADED.\n\nCheck browser console (F12) for the list.`);
                      } else if (summary.broken_urls > 0) {
                        toast.warning(message, { duration: 8000 });
                      } else {
                        toast.success(message, { duration: 5000 });
                      }
                    } catch (error) {
                      toast.error('Failed to check image URLs');
                      console.error(error);
                    }
                  }}
                  variant="outline"
                  className="rounded-none border-zinc-700 text-zinc-400 hover:text-white text-xs"
                  data-testid="check-image-urls-btn"
                >
                  CHECK ALL URLS
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/broken-images`, { headers: getAuthHeader() });
                      if (res.data.count === 0) {
                        toast.success('No broken images found!');
                      } else {
                        toast.info(`Found ${res.data.count} products with old URLs`);
                        console.log('Broken images:', res.data.products);
                      }
                    } catch (error) {
                      toast.error('Failed to scan for broken images');
                    }
                  }}
                  variant="outline"
                  className="rounded-none border-zinc-700 text-zinc-400 hover:text-white text-xs"
                  data-testid="scan-broken-images-btn"
                >
                  SCAN FOR BROKEN
                </Button>
                <Button
                  onClick={async () => {
                    if (!window.confirm('This will attempt to fix all broken image URLs.\n\nOld preview URLs will be converted to relative paths or cleared.\n\nContinue?')) return;
                    try {
                      const res = await axios.post(`${API}/admin/fix-images`, {}, { headers: getAuthHeader() });
                      toast.success(`Fixed ${res.data.fixed} URLs, cleared ${res.data.cleared} broken URLs`);
                      fetchData();
                    } catch (error) {
                      toast.error('Failed to fix images');
                    }
                  }}
                  className="rounded-none bg-tiffany-500 text-black hover:bg-tiffany-400 text-xs"
                  data-testid="fix-broken-images-btn"
                >
                  FIX BROKEN IMAGES
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-sans text-xl text-white">ORDERS ({orders.length})</h2>
              <div className="flex gap-2 flex-wrap">
                {/* Combine Shipping Mode Toggle */}
                <Button
                  onClick={() => {
                    setCombineShippingMode(!combineShippingMode);
                    if (combineShippingMode) {
                      setSelectedOrders([]); // Clear selection when exiting mode
                    }
                  }}
                  className={`rounded-none text-[10px] sm:text-xs px-2 py-1.5 transition-colors ${
                    combineShippingMode 
                      ? 'bg-tiffany-500 text-black hover:bg-tiffany-400' 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'
                  }`}
                  data-testid="combine-shipping-toggle"
                >
                  <Package className="w-3 h-3 mr-1" />
                  {combineShippingMode ? 'CANCEL COMBINE' : 'COMBINE SHIPPING'}
                </Button>
                
                {/* Apply Combined Tracking Button - Only shows when orders are selected */}
                {combineShippingMode && selectedOrders.length > 0 && (
                  <Button
                    onClick={() => {
                      // Find any selected order that already has a tracking number
                      const selectedOrderObjects = orders.filter(o => selectedOrders.includes(o.id));
                      const ordersWithTracking = selectedOrderObjects.filter(o => o.tracking_number);
                      
                      // Collect distinct tracking numbers
                      const distinctTrackers = [...new Set(ordersWithTracking.map(o => o.tracking_number))];
                      
                      if (distinctTrackers.length === 1) {
                        // Single tracking number found across selection — auto-apply, no popup
                        const source = ordersWithTracking[0];
                        const tracking = source.tracking_number;
                        const carrier = source.shipping_carrier || 'usps';
                        const targetCount = selectedOrderObjects.filter(o => o.tracking_number !== tracking).length;
                        
                        if (targetCount === 0) {
                          toast.info('All selected orders already have this tracking number');
                          return;
                        }
                        
                        if (!window.confirm(`Apply tracking "${tracking}" (from ${source.id}) to ${targetCount} other selected order(s)?`)) return;
                        
                        // Set state and submit immediately
                        setBulkTracking({ tracking_number: tracking, shipping_carrier: carrier });
                        // Call API directly with the resolved values
                        (async () => {
                          try {
                            const res = await axios.put(`${API}/orders/bulk-tracking`, {
                              order_ids: selectedOrders,
                              tracking_number: tracking,
                              shipping_carrier: carrier
                            }, { headers: getAuthHeader() });
                            toast.success(`Applied tracking to ${res.data.updated_count} orders`);
                            setSelectedOrders([]);
                            setCombineShippingMode(false);
                            setBulkTracking({ tracking_number: '', shipping_carrier: 'usps' });
                            fetchData();
                          } catch (error) {
                            toast.error('Failed to apply tracking');
                          }
                        })();
                        return;
                      }
                      
                      if (distinctTrackers.length > 1) {
                        // Multiple different tracking numbers — let admin choose via popup, pre-fill the most-recent one
                        const mostRecent = ordersWithTracking
                          .sort((a, b) => (b.shipped_at || b.updated_at || '').localeCompare(a.shipped_at || a.updated_at || ''))[0];
                        setBulkTracking({
                          tracking_number: mostRecent.tracking_number,
                          shipping_carrier: mostRecent.shipping_carrier || 'usps'
                        });
                        toast.info(`${distinctTrackers.length} different tracking numbers found — confirm which to apply`);
                        setBulkTrackingOpen(true);
                        return;
                      }
                      
                      // No order has a tracking number — fall through to manual entry
                      setBulkTrackingOpen(true);
                    }}
                    className="rounded-none bg-green-500 text-black hover:bg-green-400 text-[10px] sm:text-xs px-2 py-1.5"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    APPLY TRACKING ({selectedOrders.length})
                  </Button>
                )}
                
                {/* PRINT LABELS — uses combine-shipping selection */}
                {combineShippingMode && selectedOrders.length > 0 && (
                  <Button
                    onClick={() => {
                      const perSheet = window.prompt(
                        `Print ${selectedOrders.length} label${selectedOrders.length === 1 ? '' : 's'}.\n\nLabels per 4x6 sheet:\n1 = full size\n2 = stacked (recommended)\n4 = 2x2 grid (small)\n\nEnter 1, 2, or 4:`,
                        '2'
                      );
                      if (!perSheet) return;
                      const n = parseInt(perSheet, 10);
                      if (![1, 2, 4].includes(n)) {
                        toast.error('Please enter 1, 2, or 4');
                        return;
                      }
                      const url = `/admin/print-labels?ids=${selectedOrders.join(',')}&per_sheet=${n}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    className="rounded-none bg-white text-black hover:bg-zinc-200 text-[10px] sm:text-xs px-2 py-1.5"
                    data-testid="print-labels-btn"
                  >
                    <Printer className="w-3 h-3 mr-1" />
                    PRINT LABELS ({selectedOrders.length})
                  </Button>
                )}
                
                {/* Bulk Mark as Shipped Button - Responsive */}
                {orders.filter(o => o.status === 'pending_shipment').length > 0 && (
                  <Button
                    onClick={async () => {
                      const pendingShipment = orders.filter(o => o.status === 'pending_shipment');
                      if (!window.confirm(`Mark ${pendingShipment.length} order(s) as SHIPPED?`)) return;
                      try {
                        await axios.put(`${API}/orders/bulk-status`, {
                          order_ids: pendingShipment.map(o => o.id),
                          new_status: 'shipped'
                        }, { headers: getAuthHeader() });
                        toast.success(`${pendingShipment.length} orders marked as shipped!`);
                        fetchData();
                      } catch (error) {
                        toast.error('Failed to update orders');
                      }
                    }}
                    className="rounded-none bg-blue-500 text-white hover:bg-blue-600 text-[10px] sm:text-xs px-2 py-1.5"
                    data-testid="bulk-ship-btn"
                  >
                    <Truck className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline">MARK ALL PENDING SHIPMENT AS SHIPPED</span>
                    <span className="sm:hidden">SHIP ALL</span>
                    <span className="ml-1">({orders.filter(o => o.status === 'pending_shipment').length})</span>
                  </Button>
                )}
              </div>
            </div>
            
            {/* Bulk Tracking Dialog */}
            <Dialog open={bulkTrackingOpen} onOpenChange={setBulkTrackingOpen}>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white font-mono">COMBINE SHIPMENT</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400">
                    Apply same tracking to {selectedOrders.length} selected order(s)
                  </p>
                  <div className="space-y-2">
                    <label className="text-xs text-zinc-500 font-mono">TRACKING NUMBER</label>
                    <Input
                      value={bulkTracking.tracking_number}
                      onChange={(e) => setBulkTracking(p => ({ ...p, tracking_number: e.target.value }))}
                      placeholder="Enter tracking number"
                      className="bg-zinc-800 border-zinc-700 text-white font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-zinc-500 font-mono">CARRIER</label>
                    <Select
                      value={bulkTracking.shipping_carrier}
                      onValueChange={(v) => setBulkTracking(p => ({ ...p, shipping_carrier: v }))}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="usps">USPS</SelectItem>
                        <SelectItem value="ups">UPS</SelectItem>
                        <SelectItem value="fedex">FedEx</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleBulkTracking}
                      className="flex-1 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                    >
                      APPLY TRACKING
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setBulkTrackingOpen(false)}
                      className="rounded-none border-zinc-700"
                    >
                      CANCEL
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Order Number Management */}
            <OrderNumberManager getAuthHeader={getAuthHeader} onRefresh={fetchData} />
            
            <div className="space-y-4" data-testid="orders-table">
              {orders.map(order => (
                <div key={order.id} className="flex items-start gap-2">
                  {/* Checkbox only visible in Combine Shipping mode */}
                  {combineShippingMode && (
                    <button
                      onClick={() => {
                        if (selectedOrders.includes(order.id)) {
                          setSelectedOrders(prev => prev.filter(id => id !== order.id));
                        } else {
                          setSelectedOrders(prev => [...prev, order.id]);
                        }
                      }}
                      className={`mt-4 w-5 h-5 border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                        selectedOrders.includes(order.id)
                          ? 'bg-tiffany-500 border-tiffany-500'
                          : 'bg-transparent border-zinc-600 hover:border-tiffany-500'
                      }`}
                      data-testid={`select-order-${order.id}`}
                    >
                      {selectedOrders.includes(order.id) && (
                        <Check className="w-3 h-3 text-black" />
                      )}
                    </button>
                  )}
                  <div className="flex-1">
                    <OrderCard 
                      order={order} 
                      onUpdate={handleOrderStatusUpdate}
                      onRefresh={fetchData}
                      getAuthHeader={getAuthHeader}
                    />
                  </div>
                </div>
              ))}
              {orders.length === 0 && (
                <div className="border border-zinc-800 p-8 text-center">
                  <p className="font-mono text-zinc-500">NO ORDERS YET</p>
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h2 className="font-sans text-xl text-white">OFFERS ({offers.length})</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-[10px] sm:text-xs font-mono text-center">
                  {offerCounts.pending || 0} PENDING
                </span>
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-[10px] sm:text-xs font-mono text-center">
                  {offerCounts.user_final || 0} USER FINAL OFFERS
                </span>
                <span className="px-2 py-1 bg-tiffany-500/20 text-tiffany-400 text-[10px] sm:text-xs font-mono text-center">
                  {offerCounts.countered || 0} COUNTERED
                </span>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/offers/restore-missing-carts`, { dry_run: true }, { headers: getAuthHeader() });
                      const data = res.data;
                      if (data.total_missing > 0) {
                        const details = data.missing.map(m => `${m.user} - ${m.product} ($${m.price}) [${m.type}]`).join('\n');
                        toast.success(`FOUND ${data.total_missing} MISSING ITEM${data.total_missing > 1 ? 'S' : ''}:\n${details}`, { duration: 10000 });
                      } else {
                        toast.info('NO MISSING OFFERS/TRADES — ALL ACCEPTED DEALS ARE IN CARTS');
                      }
                    } catch (e) {
                      toast.error('FAILED TO CHECK');
                    }
                  }}
                  className="px-2 py-1 bg-orange-500/20 text-orange-400 text-[10px] sm:text-xs font-mono hover:bg-orange-500/30 transition-colors"
                  data-testid="restore-missing-offers-btn"
                >
                  CHECK MISSING
                </button>
              </div>
            </div>

            
            {offers.length === 0 ? (
              <div className="border border-zinc-800 p-8 text-center">
                <DollarSign className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="font-mono text-zinc-500">NO OFFERS YET</p>
                <p className="font-mono text-xs text-zinc-600 mt-2">
                  Offers will appear here when customers submit them
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {offers.map((offer) => (
                  <div 
                    key={offer.id} 
                    className={`border p-4 ${
                      offer.status === 'pending' ? 'border-yellow-500/50 bg-yellow-500/5' :
                      offer.status === 'countered' ? 'border-blue-500/50 bg-blue-500/5' :
                      offer.status === 'accepted' ? 'border-green-500/50 bg-green-500/5' :
                      'border-zinc-800'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex gap-3">
                        {offer.product_image && (
                          <img 
                            src={offer.product_image.startsWith('data:') ? offer.product_image : 
                                 offer.product_image.startsWith('/') ? `${'https://shopblkmrkt.com'}${offer.product_image}` : 
                                 offer.product_image}
                            alt={offer.product_name}
                            className="w-12 h-12 object-cover"
                          />
                        )}
                        <div>
                          <p className="font-mono text-sm text-white">{offer.product_name}</p>
                          <p className="font-mono text-xs text-zinc-500">{offer.user_name} ({offer.user_email})</p>
                          <p className="font-mono text-[10px] text-zinc-600">
                            {new Date(offer.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase whitespace-nowrap ${
                          offer.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          offer.status === 'countered' ? 'bg-blue-500/20 text-blue-400' :
                          offer.status === 'final_offer' ? 'bg-orange-500/20 text-orange-400' :
                          offer.status === 'user_final' ? 'bg-purple-500/20 text-purple-400' :
                          offer.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                          offer.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                          offer.status === 'expired' ? 'bg-zinc-700/20 text-zinc-500' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {offer.status === 'final_offer' ? 'FINAL OFFER' : 
                           offer.status === 'user_final' ? 'USER FINAL OFFER' : 
                           offer.status.toUpperCase().replace('_', ' ')}
                        </span>
                        {/* Show expiration for active offers */}
                        {['pending', 'countered', 'final_offer', 'user_final'].includes(offer.status) && offer.expires_at && (
                          <p className="font-mono text-[10px] text-red-400 mt-1">
                            ⏱ {(() => {
                              const expiry = new Date(offer.expires_at);
                              const now = new Date();
                              const diffMs = expiry - now;
                              if (diffMs <= 0) return 'EXPIRED';
                              const hours = Math.floor(diffMs / (1000 * 60 * 60));
                              const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                              return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
                            })()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <div>
                        <p className="font-mono text-[10px] text-zinc-500">ASKING</p>
                        <p className="font-mono text-sm text-zinc-400">${offer.product_price?.toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-mono text-[10px] text-zinc-500">OFFER</p>
                        <p className="font-mono text-lg text-yellow-400 font-bold">${offer.offer_amount?.toFixed(2)}</p>
                      </div>
                      {offer.counter_amount && (
                        <div className="text-center">
                          <p className="font-mono text-[10px] text-zinc-500">COUNTER</p>
                          <p className="font-mono text-lg text-blue-400 font-bold">${offer.counter_amount?.toFixed(2)}</p>
                        </div>
                      )}
                      {offer.user_final_amount && (
                        <div className="text-center">
                          <p className="font-mono text-[10px] text-zinc-500">USER FINAL OFFER</p>
                          <p className="font-mono text-lg text-purple-400 font-bold">${offer.user_final_amount?.toFixed(2)}</p>
                        </div>
                      )}
                      <div>
                        <p className="font-mono text-[10px] text-zinc-500">DISCOUNT</p>
                        <p className="font-mono text-sm text-tiffany-400">
                          {((1 - (offer.user_final_amount || offer.counter_amount || offer.offer_amount) / offer.product_price) * 100).toFixed(0)}% OFF
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons - For pending and user_final offers */}
                    {(offer.status === 'pending' || offer.status === 'user_final') && (
                      <div className="pt-3 border-t border-zinc-800 space-y-2">
                        {/* ACCEPT, COUNTER, DECLINE - Evenly spaced row */}
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            size="sm"
                            onClick={async () => {
                              const amountToAccept = offer.status === 'user_final' ? offer.user_final_amount : offer.offer_amount;
                              if (!window.confirm(`Accept offer of $${amountToAccept?.toFixed(2)}?`)) return;
                              try {
                                await axios.put(`${API}/admin/offers/${offer.id}/accept`, {}, { headers: getAuthHeader() });
                                toast.success(`Offer accepted! Item added to buyer's cart at $${amountToAccept?.toFixed(2)}`);
                                fetchData();
                              } catch (err) {
                                toast.error(errMsg(err, 'Failed to accept'));
                              }
                            }}
                            className="h-8 rounded-none font-mono text-xs bg-green-500 text-white hover:bg-green-400"
                          >
                            <Check className="w-3 h-3 mr-1" /> ACCEPT
                          </Button>
                          <Button
                            size="sm"
                            disabled={offer.status === 'user_final'}
                            onClick={async () => {
                              const counterAmount = prompt(`Enter counter offer (current: $${offer.offer_amount.toFixed(2)}, asking: $${offer.product_price.toFixed(2)}):`);
                              if (!counterAmount) return;
                              const amount = parseFloat(counterAmount);
                              if (isNaN(amount) || amount <= offer.offer_amount || amount >= offer.product_price) {
                                toast.error('Counter must be between offer and asking price');
                                return;
                              }
                              try {
                                await axios.put(`${API}/admin/offers/${offer.id}/counter`, 
                                  { counter_amount: amount, is_final: false },
                                  { headers: getAuthHeader() }
                                );
                                toast.success(`Counter of $${amount.toFixed(2)} sent (negotiable)`);
                                fetchData();
                              } catch (err) {
                                toast.error(errMsg(err, 'Failed to counter'));
                              }
                            }}
                            className={`h-8 rounded-none font-mono text-xs ${
                              offer.status === 'user_final' 
                                ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' 
                                : 'bg-blue-500 text-white hover:bg-blue-400'
                            }`}
                          >
                            <DollarSign className="w-3 h-3 mr-1" /> COUNTER
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (!window.confirm('Decline this offer?')) return;
                              try {
                                await axios.put(`${API}/admin/offers/${offer.id}/decline`, {}, { headers: getAuthHeader() });
                                toast.success('Offer declined');
                                fetchData();
                              } catch (err) {
                                toast.error(errMsg(err, 'Failed to decline'));
                              }
                            }}
                            className="h-8 rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-xs"
                          >
                            <X className="w-3 h-3 mr-1" /> DECLINE
                          </Button>
                        </div>
                        
                        {/* FINAL OFFER - Full width button at bottom - disabled when user already sent final offer */}
                        <Button
                          size="sm"
                          disabled={offer.status === 'user_final'}
                          onClick={async () => {
                            const counterAmount = prompt(`Enter FINAL offer (current: $${(offer.user_final_amount || offer.offer_amount).toFixed(2)}, asking: $${offer.product_price.toFixed(2)}):`);
                            if (!counterAmount) return;
                            const amount = parseFloat(counterAmount);
                            if (isNaN(amount) || amount <= (offer.user_final_amount || offer.offer_amount) || amount >= offer.product_price) {
                              toast.error('Amount must be between offer and asking price');
                              return;
                            }
                            if (!window.confirm(`Send FINAL OFFER of $${amount.toFixed(2)}?\n\nBuyer can only accept or decline.`)) return;
                            try {
                              await axios.put(`${API}/admin/offers/${offer.id}/counter`, 
                                { counter_amount: amount, is_final: true },
                                { headers: getAuthHeader() }
                              );
                              toast.success(`FINAL OFFER of $${amount.toFixed(2)} sent`);
                              fetchData();
                            } catch (err) {
                              toast.error(errMsg(err, 'Failed to send final offer'));
                            }
                          }}
                          className={`w-full h-10 rounded-none font-mono text-sm font-bold ${
                            offer.status === 'user_final'
                              ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                              : 'bg-orange-500 text-white hover:bg-orange-400'
                          }`}
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" /> FINAL OFFER
                        </Button>
                      </div>
                    )}
                    
                    {/* Delete button - available for all offers */}
                    <div className={`flex justify-end ${(offer.status === 'pending' || offer.status === 'user_final') ? 'mt-2' : 'pt-3 border-t border-zinc-800'}`}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!window.confirm(`Permanently delete this offer from ${offer.user_name}?`)) return;
                          try {
                            await axios.delete(`${API}/admin/offers/${offer.id}`, { headers: getAuthHeader() });
                            toast.success('Offer deleted');
                            fetchData();
                          } catch (err) {
                            toast.error(errMsg(err, 'Failed to delete'));
                          }
                        }}
                        className="h-7 px-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 font-mono text-xs"
                        data-testid={`delete-offer-${offer.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> DELETE
                      </Button>
                    </div>
                    
                    {offer.admin_notes && (
                      <p className="mt-2 font-mono text-xs text-zinc-500 italic">Note: {offer.admin_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Payment Plans Tab */}
          <TabsContent value="plans" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h2 className="font-sans text-xl text-white">PAYMENT PLANS ({paymentPlans.length})</h2>
              <div className="grid grid-cols-4 sm:flex gap-2">
                <span className="px-2 py-1 bg-tiffany-500/20 text-tiffany-400 text-[10px] sm:text-xs font-mono text-center">
                  {paymentPlanCounts.active || 0} ACTIVE
                </span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-[10px] sm:text-xs font-mono text-center">
                  {paymentPlanCounts.completed || 0} PAID
                </span>
                <span className="px-2 py-1 bg-zinc-500/20 text-zinc-400 text-[10px] sm:text-xs font-mono text-center">
                  {paymentPlanCounts.cancelled || 0} CANCELLED
                </span>
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-[10px] sm:text-xs font-mono text-center">
                  {paymentPlanCounts.defaulted || 0} DEFAULTED
                </span>
              </div>
            </div>

            {paymentPlans.length === 0 ? (
              <div className="border border-zinc-800 p-8 text-center">
                <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="font-mono text-zinc-500">No payment plans yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentPlans.map((plan) => {
                  const progress = plan.total_amount > 0 ? (plan.amount_paid / plan.total_amount) * 100 : 0;
                  const isOverdue = plan.status === 'active' && plan.next_payment_due && new Date() > new Date(plan.next_payment_due);
                  
                  return (
                    <div 
                      key={plan.id}
                      className={`border p-4 ${
                        plan.status === 'active' ? (isOverdue ? 'border-red-500/50 bg-red-500/5' : 'border-tiffany-500/50 bg-tiffany-500/5') :
                        plan.status === 'completed' ? 'border-green-500/50 bg-green-500/5' :
                        plan.status === 'cancelled' ? 'border-zinc-700 bg-zinc-900' :
                        'border-red-500/50 bg-red-500/5'
                      }`}
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex gap-3">
                          {plan.product_image && (
                            <img 
                              src={plan.product_image.startsWith('data:') ? plan.product_image : 
                                   plan.product_image.startsWith('/') ? `${'https://shopblkmrkt.com'}${plan.product_image}` : 
                                   plan.product_image}
                              alt={plan.product_name}
                              className="w-14 h-14 object-cover"
                            />
                          )}
                          <div>
                            {plan.order_id && (
                              <p className="font-mono text-xs text-tiffany-500 font-bold">{plan.order_id}</p>
                            )}
                            <p className="font-mono text-sm text-white">{plan.product_name}</p>
                            <p className="font-mono text-xs text-zinc-500">{plan.user_name}</p>
                            <p className="font-mono text-[10px] text-zinc-600">{plan.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${
                            plan.status === 'active' ? (isOverdue ? 'bg-red-500/20 text-red-400' : 'bg-tiffany-500/20 text-tiffany-400') :
                            plan.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            plan.status === 'cancelled' ? 'bg-zinc-700/20 text-zinc-500' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {isOverdue ? 'OVERDUE' : plan.status}
                          </span>
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs font-mono mb-1">
                          <span className="text-zinc-400">Progress</span>
                          <span className="text-white">${plan.amount_paid?.toFixed(2)} / ${plan.total_amount?.toFixed(2)}</span>
                        </div>
                        <div className="w-full bg-zinc-800 h-2">
                          <div 
                            className={`h-full transition-all ${plan.status === 'completed' ? 'bg-green-500' : 'bg-tiffany-500'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Payment Schedule */}
                      <div className="space-y-2 mb-4">
                        {plan.payments?.map((payment, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-center justify-between p-2 text-xs font-mono ${
                              payment.paid ? 'bg-green-500/10' : 'bg-zinc-800/30'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {payment.paid ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <div className="w-4 h-4 border border-zinc-600 rounded-full" />
                              )}
                              <div>
                                <span className="text-zinc-400">Payment {payment.payment_number}</span>
                                {payment.transaction_id && (
                                  <p className="text-[9px] text-zinc-600">TXN: {payment.transaction_id}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-white">${payment.amount?.toFixed(2)}</span>
                              <span className="text-zinc-600 text-[10px]">
                                {payment.paid ? `PAID ${payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : ''}` : `Due ${new Date(payment.due_date).toLocaleDateString()}`}
                              </span>
                              {plan.status === 'active' && !payment.paid && (
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    const txnId = prompt('Enter transaction ID (optional):');
                                    try {
                                      await axios.put(`${API}/admin/payment-plans/${plan.id}/record-payment`, 
                                        { payment_number: payment.payment_number, paid: true, transaction_id: txnId || null },
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success(`Payment ${payment.payment_number} recorded`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Failed to record payment');
                                    }
                                  }}
                                  className="h-6 px-2 rounded-none bg-green-500/20 text-green-400 hover:bg-green-500/30 text-[10px]"
                                >
                                  MARK PAID
                                </Button>
                              )}
                              {plan.status === 'active' && payment.paid && (
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    if (!window.confirm('Mark this payment as UNPAID?')) return;
                                    try {
                                      await axios.put(`${API}/admin/payment-plans/${plan.id}/record-payment`, 
                                        { payment_number: payment.payment_number, paid: false },
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success(`Payment ${payment.payment_number} marked unpaid`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Failed to update payment');
                                    }
                                  }}
                                  className="h-6 px-2 rounded-none bg-red-500/20 text-red-400 hover:bg-red-500/30 text-[10px]"
                                >
                                  UNDO
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      {plan.status === 'active' && (
                        <div className="flex gap-2 pt-2 border-t border-zinc-800">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (!window.confirm('Cancel this payment plan? Stock will be returned. Paid amounts are non-refundable.')) return;
                              try {
                                await axios.put(`${API}/admin/payment-plans/${plan.id}/cancel`, {}, { headers: getAuthHeader() });
                                toast.success('Payment plan cancelled');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to cancel plan');
                              }
                            }}
                            className="h-7 px-3 rounded-none border-zinc-600 text-zinc-400 hover:bg-zinc-800 text-[10px]"
                          >
                            CANCEL PLAN
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (!window.confirm('Mark as defaulted? Paid amounts are non-refundable.')) return;
                              try {
                                await axios.put(`${API}/admin/payment-plans/${plan.id}/mark-defaulted`, {}, { headers: getAuthHeader() });
                                toast.success('Plan marked as defaulted');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to update plan');
                              }
                            }}
                            className="h-7 px-3 rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10 text-[10px]"
                          >
                            MARK DEFAULTED
                          </Button>
                        </div>
                      )}

                      {/* Delete button - available for all statuses */}
                      <div className="flex justify-between pt-2 border-t border-zinc-800 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Assign this plan to YOUR account?\n\nThis will make it show in your profile.`)) return;
                            try {
                              await axios.put(`${API}/admin/payment-plans/${plan.id}/claim`, {}, { headers: getAuthHeader() });
                              toast.success('Plan assigned to your account');
                              fetchData();
                            } catch (error) {
                              toast.error(errMsg(error, 'Failed to assign plan'));
                            }
                          }}
                          className="h-7 px-3 rounded-none border-tiffany-500/50 text-tiffany-400 hover:bg-tiffany-500/10 text-[10px]"
                        >
                          ASSIGN TO ME
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`DELETE this payment plan permanently?\n\nProduct: ${plan.product_name}\nStatus: ${plan.status}\nPaid: $${plan.amount_paid?.toFixed(2)}\n\nThis cannot be undone.`)) return;
                            try {
                              await axios.delete(`${API}/admin/payment-plans/${plan.id}`, { headers: getAuthHeader() });
                              toast.success('Payment plan deleted');
                              fetchData();
                            } catch (error) {
                              toast.error(errMsg(error, 'Failed to delete plan'));
                            }
                          }}
                          className="h-7 px-3 rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10 text-[10px]"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          DELETE
                        </Button>
                      </div>

                      <p className="font-mono text-[10px] text-yellow-600 mt-3 text-center uppercase">
                        ALL PAYMENTS ARE NON-REFUNDABLE
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
          
          {/* Drop Config Tab */}
          <TabsContent value="drop" className="space-y-6">
            <h2 className="font-sans text-xl text-white">DROP CONFIGURATION</h2>
            
            <div className="border border-zinc-800 p-6 space-y-6 max-w-lg" data-testid="drop-config">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-white">DROP STATUS</p>
                  <p className="font-mono text-xs text-zinc-500">
                    {dropConfig.is_open ? 'Drop is currently OPEN' : 'Drop is currently CLOSED'}
                  </p>
                </div>
                <Switch
                  checked={dropConfig.is_open}
                  onCheckedChange={(v) => {
                    const action = v ? 'OPEN' : 'CLOSE';
                    if (window.confirm(`Are you sure you want to ${action} the drop?`)) {
                      setDropConfig(c => ({ ...c, is_open: v, scheduled_drop_time: v ? '' : c.scheduled_drop_time }));
                    }
                  }}
                  data-testid="drop-status-switch"
                />
              </div>
              
              {/* FBCON Mode Toggle */}
              <div className="flex items-center justify-between border border-purple-500/30 bg-purple-500/5 p-3 -mx-1">
                <div>
                  <p className="font-mono text-purple-400 text-sm">FBCON</p>
                  <p className="font-mono text-[10px] text-zinc-500">
                    {dropConfig.fbcon_mode ? 'FINGERBOARD CON MODE ACTIVE — Code: FBCON26, No priority shipping' : 'Normal mode'}
                  </p>
                </div>
                <Switch
                  checked={dropConfig.fbcon_mode || false}
                  onCheckedChange={(v) => {
                    if (v) {
                      if (window.confirm('ENABLE FBCON MODE?\n\n• Access code changes to FBCON26\n• Priority shipping disabled\n• Special landing message shown')) {
                        setDropConfig(c => ({ ...c, fbcon_mode: true, access_code: 'FBCON26', is_open: false }));
                      }
                    } else {
                      if (window.confirm('DISABLE FBCON MODE?\n\nAccess code reverts to BLKMRKT2492')) {
                        setDropConfig(c => ({ ...c, fbcon_mode: false, access_code: 'BLKMRKT2492' }));
                      }
                    }
                  }}
                  data-testid="fbcon-mode-switch"
                />
              </div>
              
              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">DROP NAME</label>
                <Input
                  placeholder="e.g., Winter Drop 2024"
                  value={dropConfig.drop_name || ''}
                  onChange={(e) => setDropConfig(c => ({ ...c, drop_name: e.target.value }))}
                  className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                  data-testid="drop-name-input"
                />
              </div>
              
              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">ACCESS CODE</label>
                <Input
                  placeholder="Code for closed access"
                  value={dropConfig.access_code || ''}
                  onChange={(e) => {
                    const input = e.target;
                    const start = input.selectionStart;
                    const end = input.selectionEnd;
                    setDropConfig(c => ({ ...c, access_code: e.target.value.toUpperCase() }));
                    requestAnimationFrame(() => input.setSelectionRange(start, end));
                  }}
                  className="bg-zinc-900 border-zinc-800 text-white rounded-none font-mono"
                  data-testid="drop-access-code-input"
                />
                <p className="font-mono text-xs text-zinc-600 mt-1">
                  Main access code when drop is closed
                </p>
              </div>
              
              {/* Temporary Access Codes Section */}
              <div className="border-t border-zinc-800 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-mono text-white flex items-center gap-2">
                      <Clock className="w-4 h-4 text-tiffany-500" />
                      TEMPORARY ACCESS CODES
                    </p>
                    <p className="font-mono text-xs text-zinc-500">
                      Time-limited codes for special access
                    </p>
                  </div>
                  <Dialog open={showTempCodeDialog} onOpenChange={setShowTempCodeDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="rounded-none border-tiffany-500 text-tiffany-500 hover:bg-tiffany-500 hover:text-black text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" /> NEW CODE
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-mono">CREATE TEMP ACCESS CODE</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <label className="block font-mono text-xs text-zinc-500 mb-1">CODE</label>
                          <Input
                            placeholder="e.g., VIP2024"
                            value={tempCodeForm.code}
                            onChange={(e) => {
                              const input = e.target;
                              const start = input.selectionStart;
                              const end = input.selectionEnd;
                              setTempCodeForm(f => ({ ...f, code: e.target.value.toUpperCase() }));
                              requestAnimationFrame(() => input.setSelectionRange(start, end));
                            }}
                            className="bg-zinc-800 border-zinc-700 text-white rounded-none font-mono"
                          />
                        </div>
                        <div>
                          <label className="block font-mono text-xs text-zinc-500 mb-1">DURATION</label>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  placeholder="0"
                                  min="0"
                                  max="24"
                                  value={Math.floor(tempCodeForm.duration_minutes / 60) || ''}
                                  onChange={(e) => {
                                    const hours = parseInt(e.target.value) || 0;
                                    const currentMins = tempCodeForm.duration_minutes % 60;
                                    setTempCodeForm(f => ({ ...f, duration_minutes: (hours * 60) + currentMins }));
                                  }}
                                  className="bg-zinc-800 border-zinc-700 text-white rounded-none font-mono w-16 text-center"
                                />
                                <span className="font-mono text-xs text-zinc-400">HOURS</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  placeholder="30"
                                  min="0"
                                  max="59"
                                  value={tempCodeForm.duration_minutes % 60 || ''}
                                  onChange={(e) => {
                                    const mins = parseInt(e.target.value) || 0;
                                    const currentHours = Math.floor(tempCodeForm.duration_minutes / 60);
                                    setTempCodeForm(f => ({ ...f, duration_minutes: (currentHours * 60) + mins }));
                                  }}
                                  className="bg-zinc-800 border-zinc-700 text-white rounded-none font-mono w-16 text-center"
                                />
                                <span className="font-mono text-xs text-zinc-400">MINUTES</span>
                              </div>
                            </div>
                          </div>
                          <p className="font-mono text-[10px] text-zinc-600 mt-1">
                            TOTAL: {Math.floor(tempCodeForm.duration_minutes / 60)}H {tempCodeForm.duration_minutes % 60}M ({tempCodeForm.duration_minutes} MIN)
                          </p>
                        </div>
                        <div>
                          <label className="block font-mono text-xs text-zinc-500 mb-1">MAX USES (optional)</label>
                          <Input
                            type="number"
                            placeholder="Leave empty for unlimited"
                            value={tempCodeForm.max_uses}
                            onChange={(e) => setTempCodeForm(f => ({ ...f, max_uses: e.target.value }))}
                            className="bg-zinc-800 border-zinc-700 text-white rounded-none"
                          />
                        </div>
                        <div>
                          <label className="block font-mono text-xs text-zinc-500 mb-1">NOTE (optional)</label>
                          <Input
                            placeholder="e.g., For customer John Doe"
                            value={tempCodeForm.note}
                            onChange={(e) => setTempCodeForm(f => ({ ...f, note: e.target.value }))}
                            className="bg-zinc-800 border-zinc-700 text-white rounded-none"
                          />
                        </div>
                        <Button
                          onClick={async () => {
                            if (!tempCodeForm.code) {
                              toast.error('Code is required');
                              return;
                            }
                            try {
                              await axios.post(`${API}/admin/temp-codes`, {
                                code: tempCodeForm.code,
                                duration_minutes: tempCodeForm.duration_minutes,
                                max_uses: tempCodeForm.max_uses ? parseInt(tempCodeForm.max_uses) : null,
                                note: tempCodeForm.note || null
                              }, { headers: getAuthHeader() });
                              toast.success(`Code ${tempCodeForm.code} created!`);
                              setTempCodeForm({ code: '', duration_minutes: 30, max_uses: '', note: '' });
                              setShowTempCodeDialog(false);
                              fetchData();
                            } catch (error) {
                              toast.error(errMsg(error, 'Failed to create code'));
                            }
                          }}
                          className="w-full bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                        >
                          CREATE CODE
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {/* Active Codes List */}
                {tempCodes.length > 0 ? (
                  <div className="space-y-2">
                    {tempCodes.map(tc => (
                      <div 
                        key={tc.id} 
                        className={`border p-3 ${tc.is_active ? 'border-tiffany-500/50 bg-tiffany-500/5' : 'border-zinc-800 bg-zinc-900/50 opacity-60'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-white font-bold">{tc.code}</span>
                            <Badge variant={tc.is_active ? 'default' : 'secondary'} className={`text-[10px] ${tc.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                              {tc.is_active ? 'ACTIVE' : tc.is_expired ? 'EXPIRED' : 'MAX USES'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(tc.code);
                                toast.success('Code copied!');
                              }}
                              className="h-7 px-2 text-zinc-400 hover:text-white"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedTempCode(selectedTempCode?.id === tc.id ? null : tc)}
                              className="h-7 px-2 text-zinc-400 hover:text-white"
                            >
                              {selectedTempCode?.id === tc.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm(`Delete code ${tc.code}?`)) {
                                  try {
                                    await axios.delete(`${API}/admin/temp-codes/${tc.id}`, { headers: getAuthHeader() });
                                    toast.success('Code deleted');
                                    fetchData();
                                  } catch (error) {
                                    toast.error('Failed to delete');
                                  }
                                }
                              }}
                              className="h-7 px-2 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs font-mono text-zinc-500">
                          <span>Uses: {tc.use_count}{tc.max_uses ? `/${tc.max_uses}` : ''}</span>
                          <span>Expires: {formatToEST(tc.expires_at)}</span>
                        </div>
                        {tc.note && (
                          <p className="mt-1 text-xs font-mono text-zinc-600">Note: {tc.note}</p>
                        )}
                        
                        {/* Expanded Usage Log */}
                        {selectedTempCode?.id === tc.id && (
                          <div className="mt-3 pt-3 border-t border-zinc-800">
                            <p className="font-mono text-xs text-zinc-400 mb-2">USAGE LOG ({tc.usage_log?.length || 0} uses)</p>
                            {tc.usage_log && tc.usage_log.length > 0 ? (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {tc.usage_log.map((log, i) => (
                                  <div key={i} className="text-xs font-mono bg-zinc-800/50 p-2 flex justify-between">
                                    <span className="text-zinc-400">{formatToEST(log.used_at)}</span>
                                    <span className="text-zinc-500">IP: {log.ip}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs font-mono text-zinc-600">No uses yet</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="font-mono text-xs text-zinc-600 text-center py-4">No temporary codes created</p>
                )}
              </div>
              
              {/* Scheduled Drop Time - only show when drop is closed */}
              {!dropConfig.is_open && (
                <div className="border-t border-zinc-800 pt-6">
                  <label className="block font-mono text-sm text-zinc-500 mb-2">
                    <div className="flex items-center gap-2">
                      <Timer className="w-4 h-4" />
                      SCHEDULE DROP TIME
                    </div>
                  </label>
                  <Input
                    type="datetime-local"
                    value={(() => {
                      // Convert stored ISO time to local datetime-local format
                      if (!dropConfig.scheduled_drop_time) return '';
                      try {
                        const date = new Date(dropConfig.scheduled_drop_time);
                        // Format as YYYY-MM-DDTHH:MM in local time
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      } catch {
                        return '';
                      }
                    })()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDropConfig(c => ({ 
                        ...c, 
                        scheduled_drop_time: val ? new Date(val).toISOString() : '' 
                      }));
                    }}
                    onClick={(e) => {
                      // Auto-fill with current time + 1 hour if empty (works on mobile)
                      if (!dropConfig.scheduled_drop_time) {
                        const defaultTime = new Date(Date.now() + 60 * 60 * 1000);
                        setDropConfig(c => ({ ...c, scheduled_drop_time: defaultTime.toISOString() }));
                      }
                    }}
                    onFocus={(e) => {
                      // Auto-fill with current time + 1 hour if empty
                      if (!dropConfig.scheduled_drop_time) {
                        const defaultTime = new Date(Date.now() + 60 * 60 * 1000);
                        setDropConfig(c => ({ ...c, scheduled_drop_time: defaultTime.toISOString() }));
                      }
                    }}
                    className="bg-zinc-900 border-zinc-800 text-white rounded-none font-mono [color-scheme:dark]"
                    data-testid="scheduled-drop-time-input"
                  />
                  <p className="font-mono text-xs text-zinc-600 mt-1 uppercase">
                    WHEN THIS TIME IS REACHED, THE DROP WILL AUTOMATICALLY OPEN
                  </p>
                  {dropConfig.scheduled_drop_time && (
                    <div className="mt-3 p-3 bg-tiffany-500/10 border border-tiffany-500/30">
                      <p className="font-mono text-xs text-tiffany-500">
                        DROP SCHEDULED FOR: {formatToEST(dropConfig.scheduled_drop_time)}
                      </p>
                      <Button
                        type="button"
                        onClick={() => setDropConfig(c => ({ ...c, scheduled_drop_time: '' }))}
                        className="mt-2 h-8 px-3 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 rounded-none text-xs"
                      >
                        CLEAR SCHEDULE
                      </Button>
                    </div>
                  )}
                  
                  {/* Quick schedule buttons */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[
                      { label: '+1 HR', hours: 1 },
                      { label: '+6 HRS', hours: 6 },
                      { label: '+12 HRS', hours: 12 },
                      { label: '+24 HRS', hours: 24 },
                      { label: '+2 DAYS', hours: 48 },
                    ].map(({ label, hours }) => (
                      <Button
                        key={label}
                        type="button"
                        onClick={() => {
                          const scheduled = new Date(Date.now() + hours * 60 * 60 * 1000);
                          setDropConfig(c => ({ ...c, scheduled_drop_time: scheduled.toISOString() }));
                        }}
                        className="h-7 px-2 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-tiffany-500 rounded-none text-xs font-mono"
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <Button
                onClick={handleDropConfigUpdate}
                className="w-full bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                data-testid="save-drop-config-btn"
              >
                SAVE CONFIGURATION
              </Button>
            </div>
            
            {/* Email Reminder Management Section */}
            <div className="border border-zinc-800 p-6 space-y-4 max-w-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-yellow-500" />
                    10-MINUTE REMINDER EMAILS
                  </p>
                  <p className="font-mono text-xs text-zinc-500">
                    Manage email notifications sent 10 mins before drop
                  </p>
                </div>
              </div>
              
              <div className="bg-zinc-900 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500 font-mono">Total Subscribers:</span>
                  <span className="text-white font-mono">{reminderStats?.total_subscribers || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500 font-mono">Sent (this drop):</span>
                  <span className="text-green-500 font-mono">{reminderStats?.sent || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500 font-mono">Subscribed:</span>
                  <span className="text-tiffany-500 font-mono">{reminderStats?.pending || 0}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const headers = getAuthHeader();
                      const res = await fetch(`${API}/admin/drop/reminders/stats`, { headers });
                      if (res.ok) {
                        const stats = await res.json();
                        setReminderStats(stats);
                        toast.success('Stats refreshed');
                      }
                    } catch (e) {
                      toast.error('Failed to fetch stats');
                    }
                  }}
                  className="flex-1 h-9 bg-zinc-800 text-white hover:bg-zinc-700 rounded-none text-xs font-mono"
                >
                  REFRESH STATS
                </Button>
                <Button
                  type="button"
                  onClick={async (e) => {
                    if (!window.confirm('Reset all reminders to "SUBSCRIBED" state? This will prepare all subscribers for the next drop notification.')) return;
                    
                    const btn = e.currentTarget;
                    const originalText = btn.innerText;
                    btn.innerText = 'RESETTING...';
                    btn.disabled = true;
                    
                    try {
                      const headers = getAuthHeader();
                      if (!headers.Authorization) {
                        toast.error('Not logged in - please refresh and login again');
                        return;
                      }
                      
                      // Use direct endpoint in server.py (more reliable)
                      const res = await fetch(`${API}/admin/reminders/reset`, {
                        method: 'POST',
                        headers: { 
                          ...headers,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (res.status === 401) {
                        toast.error('Session expired - please logout and login again');
                        return;
                      }
                      
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message || 'Reset complete!');
                        // Refresh stats
                        const statsRes = await fetch(`${API}/admin/drop/reminders/stats`, { headers });
                        if (statsRes.ok) setReminderStats(await statsRes.json());
                      } else {
                        toast.error(data.detail || 'Failed to reset reminders');
                      }
                    } catch (e) {
                      console.error('Reset error:', e);
                      toast.error('Network error: ' + e.message);
                    } finally {
                      btn.innerText = originalText;
                      btn.disabled = false;
                    }
                  }}
                  className="flex-1 h-9 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none text-xs font-mono disabled:opacity-50"
                >
                  RESET FOR NEW DROP
                </Button>
              </div>
              
              <Button
                type="button"
                onClick={async (e) => {
                  const btn = e.currentTarget;
                  const originalText = btn.innerText;
                  btn.innerText = 'SENDING...';
                  btn.disabled = true;
                  
                  try {
                    const headers = getAuthHeader();
                    const res = await fetch(`${API}/admin/reminders/test-send`, {
                      method: 'POST',
                      headers: { ...headers, 'Content-Type': 'application/json' }
                    });
                    const data = await res.json();
                    if (res.ok) {
                      toast.success(data.message);
                    } else {
                      toast.error(data.detail || 'Failed to send test');
                    }
                  } catch (e) {
                    toast.error('Error: ' + e.message);
                  } finally {
                    btn.innerText = originalText;
                    btn.disabled = false;
                  }
                }}
                className="w-full h-9 bg-zinc-800 text-white hover:bg-zinc-700 rounded-none text-xs font-mono disabled:opacity-50 mt-2"
              >
                SEND TEST EMAIL TO ME
              </Button>
              
              <p className="text-xs text-zinc-600 font-mono mt-2">
                Use "Reset for New Drop" after a drop ends to prepare all subscribers for the next notification.
              </p>
            </div>
            
            {/* Drop History Section */}
            <DropHistoryManager />
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Duplicate Product Confirmation Dialog */}
      <Dialog open={duplicateConfirm.open} onOpenChange={(open) => !open && setDuplicateConfirm({ open: false, productId: null, productName: '' })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">DUPLICATE PRODUCT?</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-zinc-400 font-mono text-sm">
              This will create a copy of:
            </p>
            <p className="text-tiffany-500 font-mono font-bold">
              {duplicateConfirm.productName}
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 p-3">
              <p className="text-amber-400 font-mono text-xs">
                ⚠️ The duplicate will be set to <span className="font-bold">OFFLINE</span> by default to prevent accidental sales.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setDuplicateConfirm({ open: false, productId: null, productName: '' })}
              className="rounded-none border-zinc-700 text-zinc-400"
            >
              CANCEL
            </Button>
            <Button
              onClick={() => handleDuplicateProduct(duplicateConfirm.productId)}
              className="rounded-none bg-amber-500 text-black hover:bg-amber-400"
            >
              <Copy className="w-4 h-4 mr-2" />
              DUPLICATE
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation Dialog */}
      <Dialog open={deleteProductConfirm.open} onOpenChange={(open) => !open && setDeleteProductConfirm({ open: false, productId: null })}>
        <DialogContent className="bg-zinc-900 border-red-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">DELETE PRODUCT?</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-zinc-400 font-mono text-sm">
              Are you sure you want to delete this product?
            </p>
            <div className="bg-green-500/10 border border-green-500/30 p-3">
              <p className="text-green-400 font-mono text-xs">
                ✓ Order history will be preserved - past sales data remains intact
              </p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 p-3">
              <p className="text-red-400 font-mono text-xs">
                ⚠️ This action cannot be undone
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setDeleteProductConfirm({ open: false, productId: null })}
              className="rounded-none border-zinc-700 text-zinc-400"
            >
              CANCEL
            </Button>
            <Button
              onClick={confirmDeleteProduct}
              className="rounded-none bg-red-500 text-white hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              DELETE
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
